/**
 * Schedule contracts (BUILD-PLAN 2.4 "Scheduling", 3.5, M03, 5.3).
 *
 * Wave-1 interface: `Appointment`, `AppointmentEvent`, `ScheduleQuery`,
 * `MoveRequest`, `SlotLease`, plus the typed appointment commands that the
 * schedule API puts in the outbox (BUILD-PLAN 2.5.3).
 *
 * Open Dental semantics (ADR-0004, BUILD-PLAN D4). Numeric values below are
 * Open Dental's own and must never be renumbered; the authoritative copy is
 * `@velora/domain/od-enums` (WP0-DATA) and a test pins these to the same
 * numbers. Source: Open Dental database documentation, table `appointment`
 * (columns AptStatus, Priority, Pattern, Confirmed) and table `procedurelog`
 * (ProcStatus), schema version 24.3 (opendental.com, "Database Documentation").
 *
 * Velora canonical vocabularies (flow, confirmation, missed) are mapped to and
 * from each PMS in the adapter, never in UI code. Confirmation in OD is a
 * per-office `definition` row (DefCat ApptConfirmed), so it is resolved per
 * office and kept raw in `confirmationPmsDef`, never hard-coded.
 */
import { z } from 'zod';
import { FreshnessSchema } from './health.js';
import { ApptStatusDefSchema } from './apptStatus.js';
import {
  IanaTimeZoneSchema,
  IdempotencyKeySchema,
  IdSchema,
  InstantSchema,
  LocalDateSchema,
  LocationOwnedSchema,
  PmsAdapterSchema,
  PmsSystemSchema,
  refineSyncEnvelope,
  RowMetaSchema,
  RowVersionSchema,
  SyncEnvelopeShape,
  WriteBackStateSchema,
} from './sync.js';

/* ------------------------------------------------------------------------ */
/* Open Dental enums used by the schedule                                    */
/* ------------------------------------------------------------------------ */

/**
 * OD `ApptStatus`. 0 None is never stored and 4 ASAP is obsolete in OD
 * (replaced by `Priority`), so neither is valid here; this matches the
 * `appointment.apt_status` check constraint.
 */
export const OD_APT_STATUS = {
  Scheduled: 1,
  Complete: 2,
  UnschedList: 3,
  Broken: 5,
  Planned: 6,
  PtNote: 7,
  PtNoteCompleted: 8,
} as const;
export type OdAptStatusName = keyof typeof OD_APT_STATUS;
export const AptStatusSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(5),
  z.literal(6),
  z.literal(7),
  z.literal(8),
]);
export type AptStatus = z.infer<typeof AptStatusSchema>;

/** OD `ApptPriority`: 0 Normal, 1 ASAP. */
export const OD_APT_PRIORITY = { Normal: 0, ASAP: 1 } as const;
export const AptPrioritySchema = z.union([z.literal(0), z.literal(1)]);
export type AptPriority = z.infer<typeof AptPrioritySchema>;

/**
 * OD `ProcStat` (procedurelog.ProcStatus): 1 TP (treatment planned),
 * 2 C (complete), 3 EC (existing current provider), 4 EO (existing other
 * provider), 5 R (referred out), 6 D (deleted), 7 Cn (condition),
 * 8 TPi (treatment planned inactive).
 */
export const OD_PROC_STAT = { TP: 1, C: 2, EC: 3, EO: 4, R: 5, D: 6, Cn: 7, TPi: 8 } as const;
export type OdProcStatName = keyof typeof OD_PROC_STAT;
export const ProcStatSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal(7),
  z.literal(8),
]);
export type ProcStat = z.infer<typeof ProcStatSchema>;

/* ------------------------------------------------------------------------ */
/* Velora canonical vocabularies (BUILD-PLAN 2.4)                            */
/* ------------------------------------------------------------------------ */

/** Patient flow through the visit, in order. */
export const FLOW_STATUSES = [
  'scheduled',
  'late',
  'here',
  'ready',
  'chair',
  'checkout',
  'complete',
] as const;
export const FlowStatusSchema = z.enum(FLOW_STATUSES);
export type FlowStatus = z.infer<typeof FlowStatusSchema>;

export const CONFIRMATION_STATUSES = [
  'unconfirmed',
  'confirmed',
  'left_message',
  'unreachable',
  'sent_text',
  'sent_email',
  /** Line busy (Eaglesoft B). */
  'phone_busy',
  /** Waiting for the patient to call back (Eaglesoft W). */
  'waiting_callback',
  /** Any other PMS confirmation state (Eaglesoft O); the raw value stays in `confirmationPmsDef`. */
  'other',
] as const;
export const ConfirmationStatusSchema = z.enum(CONFIRMATION_STATUSES);
export type ConfirmationStatus = z.infer<typeof ConfirmationStatusSchema>;

/** How a confirmation was obtained (confirm-remind 4.3). */
export const ConfirmedViaSchema = z.enum(['sms', 'email', 'voice', 'link', 'staff', 'pms']);
export type ConfirmedVia = z.infer<typeof ConfirmedViaSchema>;

/** Why an appointment is Broken (OD AptStatus 5). */
export const MissedReasonSchema = z.enum(['broken', 'no_show', 'cancelled_by_office']);
export type MissedReason = z.infer<typeof MissedReasonSchema>;

/**
 * What the front desk picks in the Break dialog (sched-manage EX-07). The PMS
 * and the metrics keep the coarser {@link MissedReason} (a patient
 * cancellation is OD "Broken" either way; metrics split it by notice), so
 * each type maps onto one missed reason and Velora keeps the type, the notice
 * hours and the fee code beside it (`appointment_cancel_detail`).
 */
export const CANCEL_TYPES = [
  'cancelled_by_patient',
  'late_cancel',
  'no_show',
  'cancelled_by_office',
] as const;
export const CancelTypeSchema = z.enum(CANCEL_TYPES);
export type CancelType = z.infer<typeof CancelTypeSchema>;

export const CANCEL_TYPE_MISSED: Readonly<Record<CancelType, MissedReason>> = Object.freeze({
  cancelled_by_patient: 'broken',
  late_cancel: 'broken',
  no_show: 'no_show',
  cancelled_by_office: 'cancelled_by_office',
});

/** The cancel type a plain missed reason stands for (no type sent). */
export const MISSED_CANCEL_TYPE: Readonly<Record<MissedReason, CancelType>> = Object.freeze({
  broken: 'late_cancel',
  no_show: 'no_show',
  cancelled_by_office: 'cancelled_by_office',
});

/**
 * CDT missed / cancelled appointment codes (D9986 missed, D9987 cancelled).
 * Velora only records the code with the break; posting the fee is the money
 * module's job (NEW-WF-10), never the schedule's.
 */
export const MISSED_FEE_CODES = Object.freeze({ missed: 'D9986', cancelled: 'D9987' });

/** The fee code a cancel type suggests (none for an office cancellation). */
export function suggestedFeeCode(type: CancelType): string | null {
  if (type === 'no_show' || type === 'late_cancel') return MISSED_FEE_CODES.missed;
  if (type === 'cancelled_by_patient') return MISSED_FEE_CODES.cancelled;
  return null;
}

/** A CDT code or an office's own fee code (letters, digits, dot, dash; up to 16). */
export const CancelFeeCodeSchema = z
  .string()
  .trim()
  .regex(
    /^[A-Za-z0-9][A-Za-z0-9.-]{0,15}$/,
    'a fee code is up to 16 letters, digits, dots or dashes',
  );

/** Hours of notice the patient gave (0 = none); up to a year. */
export const NoticeHoursSchema = z.number().int().min(0).max(8760);

/* ------------------------------------------------------------------------ */
/* Clinical identifiers shown on cards                                       */
/* ------------------------------------------------------------------------ */

/**
 * ADA CDT procedure code: "D" followed by four digits (ADA, Code on Dental
 * Procedures and Nomenclature). Offices may also have non-CDT custom codes,
 * so `AppointmentProcedure.code` is looser and this schema is for places that
 * require a true CDT code.
 */
export const CdtCodeSchema = z.string().regex(/^D\d{4}$/, 'CDT codes are D followed by 4 digits');

/**
 * Tooth number as Open Dental stores it: Universal 1..32 (permanent), A..T
 * (primary), supernumerary 51..82 (permanent, = tooth + 50) and AS..TS
 * (primary). Velora normalises other PMS numbering to this in the adapter.
 */
export const ToothNumberSchema = z
  .string()
  .regex(
    /^(?:[1-9]|[12]\d|3[0-2]|5[1-9]|[67]\d|8[0-2]|[A-T]|[A-T]S)$/,
    'tooth must be Universal 1-32, A-T, 51-82 or AS-TS',
  );
export type ToothNumber = z.infer<typeof ToothNumberSchema>;

/** Canonical surface order: Open Dental order M O I D B F V L (Tooth.SurfTidyFromDisplayToDb), each at most once. */
export const SURFACE_ORDER = 'MOIDBFVL';
export const SurfacesSchema = z
  .string()
  .min(1)
  .max(8)
  .refine((s) => {
    let last = -1;
    for (const ch of s) {
      const i = SURFACE_ORDER.indexOf(ch);
      if (i <= last) return false;
      last = i;
    }
    return true;
  }, 'surfaces must be distinct letters of MOIDBFVL in canonical order');
export type Surfaces = z.infer<typeof SurfacesSchema>;

/**
 * Open Dental `procedurelog.Surf` also carries the treatment area for
 * procedures that are not per-surface: "Surfaces, or use "UL" etc for
 * quadrant, "2" etc for sextant, "U","L" for arches" (OD database
 * documentation 24.3, table procedurelog; TreatmentArea 4 Quad, 5 Sextant,
 * 6 Arch). Per-quadrant CDT codes such as D4341/D4342 (scaling and root
 * planing) are stored this way, so an appointment card must accept them.
 * US sextants are 1..6 (1 UR, 2 UA, 3 UL, 4 LL, 5 LA, 6 LR). "L" is both the
 * lingual surface and the lower arch; the procedure code's treat area tells
 * them apart.
 */
export const QUADRANTS = ['UR', 'UL', 'LR', 'LL'] as const;
export const ARCHES = ['U', 'L'] as const;
const AREA_RE = /^(?:UR|UL|LR|LL|[1-6]|U|L)$/;
export const TreatmentAreaCodeSchema = z
  .string()
  .regex(AREA_RE, 'area is a quadrant (UR, UL, LR, LL), US sextant (1-6) or arch (U, L)');

/** `surfaces` on a procedure: tooth surfaces, or a quadrant, sextant or arch code. */
export const ProcedureSurfOrAreaSchema = z.union([SurfacesSchema, TreatmentAreaCodeSchema]);

/** True when the value is an area code (quadrant, sextant, arch U) rather than surfaces. */
export function isTreatmentAreaCode(s: string): boolean {
  return AREA_RE.test(s) && s !== 'L';
}

/** A procedure attached to an appointment (the card's code list). */
export const AppointmentProcedureSchema = z
  .object({
    procedureId: IdSchema,
    /** CDT code (D0120) or an office's custom code; OD ProcCode is varchar(15). */
    code: z.string().min(1).max(15),
    abbreviation: z.string().max(20).nullable(),
    /** The code's short description (procedure_code.description), for the hover card. Optional for older servers. */
    description: z.string().max(80).nullable().optional(),
    tooth: ToothNumberSchema.nullable(),
    /** Surfaces (MOIDBFVL order) or a quadrant/sextant/arch code (OD `Surf`). */
    surfaces: ProcedureSurfOrAreaSchema.nullable(),
    status: ProcStatSchema,
    /** Fee in integer cents (BUILD-PLAN 5.7). Null when the viewer lacks `money.view`. */
    feeCents: z.number().int().nullable(),
    /**
     * `velora`: Velora attached it to this appointment (NEW-WF-01). At a PMS
     * office that link is kept in Velora only (no PulseSync command sets
     * ProcedureLog.AptNum yet); `pms`: the link came from the PMS.
     */
    attachedIn: z.enum(['pms', 'velora']).optional(),
    /** Created in Velora (at a PMS office: a draft that is not in the PMS). */
    veloraDraft: z.boolean().optional(),
    /**
     * A procedure code the PMS appointment lists with no charted procedure
     * behind it (Dentrix appointment code slot). `procedureId` is then the
     * slot's id, not a procedure: it cannot be attached, detached or carried.
     */
    pmsSlot: z.boolean().optional(),
    /** The fee is the appointment's PMS amount spread over its codes, not a schedule fee. */
    feeEstimated: z.boolean().optional(),
  })
  .superRefine((p, ctx) => {
    if (p.tooth !== null && p.surfaces !== null && isTreatmentAreaCode(p.surfaces)) {
      ctx.addIssue({
        code: 'custom',
        path: ['surfaces'],
        message: 'a quadrant, sextant or arch procedure has no tooth number',
      });
    }
  });
export type AppointmentProcedure = z.infer<typeof AppointmentProcedureSchema>;

/* ------------------------------------------------------------------------ */
/* Time pattern                                                              */
/* ------------------------------------------------------------------------ */

/** Minutes per pattern character. OD stores Pattern at 5 minutes per char. */
export const PATTERN_MINUTES_PER_CHAR = 5;

/**
 * OD `Pattern`: `X` = provider time, `/` = assistant or hygienist time, one
 * character per 5 minutes (`/XX//` is a 25-minute visit with 10 provider
 * minutes). Parsing and rendering live in `@velora/domain/time`. The OD column
 * is varchar(255) (OD database documentation 24.3, table appointment), so a
 * pattern longer than 255 characters (21 h 15 min) cannot round-trip.
 */
export const TIME_PATTERN_MAX_CHARS = 255;
export const TimePatternSchema = z
  .string()
  .min(1)
  .max(TIME_PATTERN_MAX_CHARS)
  .regex(/^[X/]+$/, 'time pattern uses only X and /');

/** Appointment length: positive, a multiple of 5 minutes, at most one day. */
export const LengthMinSchema = z
  .number()
  .int()
  .positive()
  .max(1440)
  .refine((n) => n % PATTERN_MINUTES_PER_CHAR === 0, 'length must be a multiple of 5 minutes');

function checkPatternLength(
  v: { lengthMin?: number | undefined; timePattern?: string | null | undefined },
  ctx: z.RefinementCtx,
): void {
  if (typeof v.timePattern === 'string' && typeof v.lengthMin === 'number') {
    if (v.timePattern.length * PATTERN_MINUTES_PER_CHAR !== v.lengthMin) {
      ctx.addIssue({
        code: 'custom',
        path: ['timePattern'],
        message: `time pattern covers ${v.timePattern.length * PATTERN_MINUTES_PER_CHAR} min but length is ${v.lengthMin}`,
      });
    }
  }
}

/* ------------------------------------------------------------------------ */
/* Appointment                                                               */
/* ------------------------------------------------------------------------ */

/**
 * Card-level patient reference. Privacy mode is server-side (BUILD-PLAN 2.6):
 * when the request is masked, `displayName` is null and only `initials` is
 * sent.
 */
export const AppointmentPatientRefSchema = z.object({
  id: IdSchema,
  displayName: z.string().min(1).max(200).nullable(),
  initials: z.string().min(1).max(4),
  /*
   * Block lines the PMS modes draw (requests-dentrix I-3, requests-eaglesoft
   * D2). Optional and PHI: sent only when names are (privacy mode off and
   * `patient.view`), absent otherwise.
   */
  homePhone: z.string().max(40).nullable().optional(),
  workPhone: z.string().max(40).nullable().optional(),
  cellPhone: z.string().max(40).nullable().optional(),
  ageYears: z.number().int().min(0).max(150).nullable().optional(),
  /** `F`, `M` or `U` (unknown or other). */
  sex: z.enum(['F', 'M', 'U']).nullable().optional(),
  chartNumber: z.string().max(40).nullable().optional(),
  /** Primary carrier name; null for self pay or when plans are not synced. */
  insuranceLabel: z.string().max(200).nullable().optional(),
  /**
   * Whether `insuranceLabel` can be trusted (S7): `on_file` (a plan is on
   * file), `none` (no plan: self pay), `not_synced` (this office's PMS feed
   * has no insurance yet, so a null label means "unknown", not self pay).
   */
  insuranceState: z.enum(['on_file', 'none', 'not_synced']).optional(),
  email: z.string().max(320).nullable().optional(),
  /** A non-medical patient alert is active (Dentrix yellow flag, OD popup). */
  hasPatientAlert: z.boolean().optional(),
  /** Active medical, allergy, medication and premed alert texts. */
  medicalAlerts: z.array(z.string().max(500)).max(20).optional(),
  /**
   * The patient's household (synced from the PMS guarantor after PS-03), so
   * lists group a family by household, never by surname. Sent with the names.
   */
  householdId: IdSchema.nullable().optional(),
});
export type AppointmentPatientRef = z.infer<typeof AppointmentPatientRefSchema>;

/** Readiness chips on the card (BUILD-PLAN 5.3 WP1-SCHED-API). */
export const AppointmentReadinessSchema = z.object({
  insuranceVerified: z.enum(['yes', 'no', 'unknown', 'not_applicable']),
  formsComplete: z.enum(['yes', 'no', 'unknown', 'not_applicable']),
  /** Household balance due in integer cents; null without `money.view`. */
  balanceDueCents: z.number().int().nullable(),
  hasMedicalAlert: z.boolean(),
  premedRequired: z.boolean(),
  /** State of the lab case for this visit (BUILD-PLAN 3.5 "lab case (received state)"). */
  labCase: z.enum(['none', 'created', 'sent', 'received', 'checked']).optional(),
  /**
   * NEW-WF-12 (LR-L2, LR-L3): `after_ready_by` (amber) when the case is due
   * after this seat visit's ready-by date, `late` (red ring) when it is not
   * back at 08:00 the business day before or its due date passed.
   */
  labTiming: z.enum(['on_time', 'after_ready_by', 'late']).optional(),
  /** The case's office-local due date, for the chip tooltip. */
  labDueDate: z.iso.date().nullable().optional(),
  /** No-shows in the last 24 months (BUILD-PLAN 3.5 "no-show history count"). */
  noShowCount: z.number().int().nonnegative().optional(),
  /** PE-15: an unfinished Velora perio exam (draft, last 72 h) waits for this patient. */
  perioDraft: z.boolean().optional(),
});
export type AppointmentReadiness = z.infer<typeof AppointmentReadinessSchema>;

const AppointmentObject = z.object({
  id: IdSchema,
  ...LocationOwnedSchema.shape,
  patientId: IdSchema.nullable(),
  operatoryId: IdSchema.nullable(),
  providerId: IdSchema.nullable(),
  /** Hygienist (OD ProvHyg). */
  secondaryProviderId: IdSchema.nullable(),
  appointmentTypeId: IdSchema.nullable(),
  /** Null for unscheduled or planned appointments. */
  startsAt: InstantSchema.nullable(),
  lengthMin: LengthMinSchema,
  timePattern: TimePatternSchema.nullable(),
  aptStatus: AptStatusSchema,
  priority: AptPrioritySchema,
  flowStatus: FlowStatusSchema,
  confirmation: ConfirmationStatusSchema,
  /** The PMS's own confirmation value (OD DefNum as a string), kept raw. */
  confirmationPmsDef: z.string().max(64).nullable(),
  /** The office's own label for that value (a Dentrix status code text), when the PMS sends one. */
  confirmationPmsLabel: z.string().max(64).nullable().optional(),
  missedReason: MissedReasonSchema.nullable(),
  arrivedAt: InstantSchema.nullable(),
  seatedAt: InstantSchema.nullable(),
  dismissedAt: InstantSchema.nullable(),
  isPinned: z.boolean(),
  isNewPatient: z.boolean(),
  note: z.string().max(4000).nullable(),
  /** Assistant on the `/` segments (WF-14); Velora-only. Optional for older servers. */
  assistantId: IdSchema.nullable().optional(),
  /** Minutes the patient is asked to arrive early (SB-12); Velora-only. */
  arriveEarlyMin: z.number().int().min(0).max(120).nullable().optional(),
  labCaseId: IdSchema.nullable(),
  procedures: z.array(AppointmentProcedureSchema),
  ...RowMetaSchema.shape,
  ...SyncEnvelopeShape,
  writeBack: WriteBackStateSchema,
});

type AppointmentShape = z.infer<typeof AppointmentObject>;

/** Invariants mirrored from the `appointment` table checks plus flow order. */
function refineAppointment(a: AppointmentShape, ctx: z.RefinementCtx): void {
  refineSyncEnvelope(a, ctx);
  checkPatternLength(a, ctx);
  if (a.missedReason !== null && a.aptStatus !== OD_APT_STATUS.Broken) {
    ctx.addIssue({
      code: 'custom',
      path: ['missedReason'],
      message: 'missedReason is only valid on Broken (aptStatus 5) appointments',
    });
  }
  // PMS rows are shown as the PMS holds them: Dentrix/Open Dental allow flow
  // stamps out of order (seated before arrived after a correction) and booked
  // rows whose operatory is unmapped. One such row must not fail the whole
  // schedule response (2026-10-19: three offices' days refused). Velora's own
  // rows keep both invariants.
  const fromPms = a.origin === 'pms';
  if (
    !fromPms &&
    (a.aptStatus === OD_APT_STATUS.Scheduled || a.aptStatus === OD_APT_STATUS.Complete) &&
    (a.startsAt === null || a.operatoryId === null)
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['startsAt'],
      message: 'scheduled and complete appointments need a start time and an operatory',
    });
  }
  if (
    a.aptStatus !== OD_APT_STATUS.PtNote &&
    a.aptStatus !== OD_APT_STATUS.PtNoteCompleted &&
    a.patientId === null
  ) {
    ctx.addIssue({ code: 'custom', path: ['patientId'], message: 'appointments need a patient' });
  }
  const order: [keyof AppointmentShape, string | null][] = [
    ['arrivedAt', a.arrivedAt],
    ['seatedAt', a.seatedAt],
    ['dismissedAt', a.dismissedAt],
  ];
  let prev: string | null = null;
  for (const [key, value] of order) {
    if (value === null) continue;
    if (!fromPms && prev !== null && Date.parse(value) < Date.parse(prev)) {
      ctx.addIssue({
        code: 'custom',
        path: [key],
        message: `${key} is earlier than the previous flow stamp`,
      });
    }
    prev = value;
  }
  // pending, sent and unknown mean a command is in flight (BUILD-PLAN 2.5.4
  // step 2); applied clears pending_command_id and waits for the read-back.
  const inFlight = a.writeBack === 'pending' || a.writeBack === 'sent' || a.writeBack === 'unknown';
  if (inFlight && a.pendingCommandId === null) {
    ctx.addIssue({
      code: 'custom',
      path: ['pendingCommandId'],
      message: `writeBack "${a.writeBack}" requires pendingCommandId`,
    });
  }
  if (a.writeBack === 'synced' && a.pendingCommandId !== null) {
    ctx.addIssue({
      code: 'custom',
      path: ['pendingCommandId'],
      message: 'a synced row has no command in flight',
    });
  }
}

/** A stored appointment as the schedule API returns it. */
export const AppointmentSchema = AppointmentObject.superRefine(refineAppointment);
export type Appointment = z.infer<typeof AppointmentSchema>;

/** Grid card: the appointment plus display-only joins. */
export const AppointmentCardSchema = AppointmentObject.extend({
  patient: AppointmentPatientRefSchema.nullable(),
  appointmentTypeName: z.string().max(100).nullable(),
  /** Design token name for the type colour (`appt-1`..`appt-40`), never a raw colour. */
  colorToken: z
    .string()
    .regex(/^appt-([1-9]|[1-3]\d|40)$/)
    .nullable(),
  readiness: AppointmentReadinessSchema.nullable(),
  /**
   * A Broken tile whose patient has since been booked again: the start of that
   * next booking ("rebooked 1/19", sched-move SM-12). Absent or null otherwise.
   */
  rebookedAt: InstantSchema.nullable().optional(),
  /**
   * The PMS's own scheduled amount for the visit in integer cents (Dentrix
   * `appt.amt`, synced as `appointment.pms_scheduled_amount_cents`). Null when
   * the PMS sends none or the viewer lacks `money.view`; absent on older servers.
   */
  scheduledAmountCents: z.number().int().nonnegative().nullable().optional(),
  /**
   * The visit's estimated value in integer cents when no procedure is
   * attached: the default fees of its appointment type's codes (SV-18 "est.",
   * the same figure as the day's `totals.estimatedCents`). Null when a
   * procedure is attached, the type has no fees, or the viewer lacks
   * `money.view`; absent on older servers.
   */
  estimatedFeeCents: z.number().int().nonnegative().nullable().optional(),
}).superRefine(refineAppointment);
export type AppointmentCard = z.infer<typeof AppointmentCardSchema>;

/* ------------------------------------------------------------------------ */
/* Appointment events (status history with actor)                            */
/* ------------------------------------------------------------------------ */

export const APPOINTMENT_EVENT_KINDS = [
  'created',
  'moved',
  'resized',
  'provider_changed',
  'operatory_changed',
  'status_changed',
  'flow_changed',
  'confirmation_changed',
  'broken',
  'pinned',
  'unpinned',
  'note_changed',
  'deleted',
  'restored',
  'pms_edit',
  'write_back',
  /** Procedures attached or detached (NEW-WF-01); `to` lists the codes now attached. */
  'procedures_changed',
  // NEW-WF-06: manager time corrections, Ready for Dr, left without being seen.
  'flow_corrected',
  'ready_for_doctor',
  'left_without_being_seen',
] as const;
export const AppointmentEventKindSchema = z.enum(APPOINTMENT_EVENT_KINDS);
export type AppointmentEventKind = z.infer<typeof AppointmentEventKindSchema>;

export const AppointmentEventActorKindSchema = z.enum(['user', 'ai', 'automation', 'pms']);
export type AppointmentEventActorKind = z.infer<typeof AppointmentEventActorKindSchema>;

/** Append-only history row (`appointment_event`). */
export const AppointmentEventSchema = z
  .object({
    id: IdSchema,
    ...LocationOwnedSchema.shape,
    appointmentId: IdSchema,
    kind: AppointmentEventKindSchema,
    /** Previous and new value, serialised as text (ISO time, op id, status). */
    fromValue: z.string().max(500).nullable(),
    toValue: z.string().max(500).nullable(),
    actorKind: AppointmentEventActorKindSchema,
    actorUserId: IdSchema.nullable(),
    /** Denormalised for the history list ("Moved by Dana"). */
    actorName: z.string().max(200).nullable(),
    occurredAt: InstantSchema,
    /** Outbox command this change produced, when written back. */
    commandId: IdSchema.nullable(),
    /** For `confirmation_changed`: how it was obtained. */
    via: ConfirmedViaSchema.nullable(),
    /** For SMS/email confirmations: the interaction that carried the reply. */
    interactionId: IdSchema.nullable(),
    /** Why a conflict was overridden (a third concurrent X, SB-06). Optional for older servers. */
    reason: z.string().max(500).nullable().optional(),
  })
  .superRefine((e, ctx) => {
    if (e.actorKind === 'user' && e.actorUserId === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['actorUserId'],
        message: 'user events need actorUserId',
      });
    }
    if (e.via !== null && e.kind !== 'confirmation_changed') {
      ctx.addIssue({
        code: 'custom',
        path: ['via'],
        message: 'via is only set on confirmation_changed',
      });
    }
  });
export type AppointmentEvent = z.infer<typeof AppointmentEventSchema>;

/* ------------------------------------------------------------------------ */
/* Schedule query                                                            */
/* ------------------------------------------------------------------------ */

/** Longest range one query may cover (6 weeks covers the month strip). */
export const SCHEDULE_QUERY_MAX_DAYS = 42;

/** Most appointment ids one live-delta range read may name (`appointmentIds`). */
export const SCHEDULE_DELTA_MAX_IDS = 50;

const DAY_MS = 86_400_000;

/**
 * `GET /v1/schedule/appointments`. Dates are office-local calendar days
 * (inclusive); the server resolves them in the office's time zone so DST days
 * have 23 or 25 hours. The schedule grid is `officeScope: 'location'`, so a
 * query names exactly one office.
 */
export const ScheduleQuerySchema = z
  .object({
    locationId: IdSchema,
    from: LocalDateSchema,
    to: LocalDateSchema,
    operatoryIds: z.array(IdSchema).max(200).optional(),
    providerIds: z.array(IdSchema).max(200).optional(),
    /**
     * Only these appointments (the live delta: the cards an `appointment`
     * event names). An id missing from the reply is no longer in this range
     * (moved, deleted or a status the grid does not draw).
     */
    appointmentIds: z.array(IdSchema).min(1).max(SCHEDULE_DELTA_MAX_IDS).optional(),
    /** Defaults to Scheduled, Complete and Broken (what the grid draws). */
    aptStatuses: z.array(AptStatusSchema).min(1).optional(),
    includeBlockouts: z.boolean().default(true),
    includeLeases: z.boolean().default(true),
  })
  .strict()
  .superRefine((q, ctx) => {
    const span = (Date.parse(q.to) - Date.parse(q.from)) / DAY_MS;
    if (span < 0) {
      ctx.addIssue({ code: 'custom', path: ['to'], message: 'to must not be before from' });
    } else if (span + 1 > SCHEDULE_QUERY_MAX_DAYS) {
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: `a schedule query covers at most ${SCHEDULE_QUERY_MAX_DAYS} days`,
      });
    }
  });
export type ScheduleQueryInput = z.input<typeof ScheduleQuerySchema>;
export type ScheduleQuery = z.output<typeof ScheduleQuerySchema>;

/** Statuses the grid draws when `aptStatuses` is omitted. */
export const DEFAULT_GRID_APT_STATUSES: readonly AptStatus[] = [
  OD_APT_STATUS.Scheduled,
  OD_APT_STATUS.Complete,
  OD_APT_STATUS.Broken,
];

/**
 * Blocked time on the grid (`blockout`). A recurring blockout is returned
 * once per occurrence in the queried range, each with the series `id`.
 */
export const BlockoutSchema = z.object({
  id: IdSchema,
  ...LocationOwnedSchema.shape,
  blockoutType: z.string().min(1).max(100),
  operatoryIds: z.array(IdSchema),
  providerId: IdSchema.nullable(),
  startsAt: InstantSchema,
  endsAt: InstantSchema,
  note: z.string().max(1000).nullable(),
  /** RFC 5545 subset (`FREQ=WEEKLY;INTERVAL=2;BYDAY=MO,WE;UNTIL=20261231`), null for one-off. */
  recurrenceRule: z.string().max(200).nullable().optional(),
  /** False for blockouts mirrored from the PMS (edit them there). */
  editable: z.boolean().optional(),
  version: RowVersionSchema.optional(),
});
export type Blockout = z.infer<typeof BlockoutSchema>;

/* ------------------------------------------------------------------------ */
/* Slot leases (BUILD-PLAN 2.5.5: prevent, don't resolve)                    */
/* ------------------------------------------------------------------------ */

/** Default lease TTL: 5 minutes (BUILD-PLAN 2.5.5). */
export const SLOT_LEASE_DEFAULT_TTL_SECONDS = 300;
/** Text-offer holds run up to 15 minutes by default (sched-move 4.4); hard cap 1 hour. */
export const SLOT_LEASE_MAX_TTL_SECONDS = 3600;

/**
 * Who holds a lease, encoded in the `slot_lease.holder` text column as
 * `<kind>:<id>`: a staff user mid-drag, an online-booking session or a text
 * offer awaiting a YES.
 */
export const SlotLeaseHolderSchema = z
  .string()
  .regex(/^(user|booking|offer|system):[A-Za-z0-9._-]{1,100}$/, 'holder is <kind>:<id>');

export const SlotLeaseSchema = z
  .object({
    id: IdSchema,
    ...LocationOwnedSchema.shape,
    operatoryId: IdSchema,
    providerId: IdSchema.nullable(),
    startsAt: InstantSchema,
    endsAt: InstantSchema,
    expiresAt: InstantSchema,
    holder: SlotLeaseHolderSchema,
    /** Shown on a refused drop: "being booked by Kim". No patient data. */
    holderDisplayName: z.string().max(100).nullable(),
    createdAt: InstantSchema,
    /** All-or-nothing group, for example "Book household" (SB-15). */
    groupId: IdSchema.nullable().optional(),
  })
  .superRefine((l, ctx) => {
    if (Date.parse(l.endsAt) <= Date.parse(l.startsAt)) {
      ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'endsAt must be after startsAt' });
    }
    if (Date.parse(l.expiresAt) <= Date.parse(l.createdAt)) {
      ctx.addIssue({
        code: 'custom',
        path: ['expiresAt'],
        message: 'expiresAt must be after createdAt',
      });
    }
    if (Date.parse(l.expiresAt) - Date.parse(l.createdAt) > SLOT_LEASE_MAX_TTL_SECONDS * 1000) {
      ctx.addIssue({
        code: 'custom',
        path: ['expiresAt'],
        message: `a lease is held at most ${SLOT_LEASE_MAX_TTL_SECONDS} s`,
      });
    }
  });
export type SlotLease = z.infer<typeof SlotLeaseSchema>;

/** `POST /v1/schedule/leases`. */
export const SlotLeaseRequestSchema = z
  .object({
    locationId: IdSchema,
    operatoryId: IdSchema,
    providerId: IdSchema.nullable().default(null),
    startsAt: InstantSchema,
    endsAt: InstantSchema,
    ttlSeconds: z
      .number()
      .int()
      .min(30)
      .max(SLOT_LEASE_MAX_TTL_SECONDS)
      .default(SLOT_LEASE_DEFAULT_TTL_SECONDS),
    purpose: z.enum(['drag', 'booking', 'offer']),
    /** Joins an all-or-nothing group of holds (SB-15); release them together. */
    groupId: IdSchema.optional(),
  })
  .strict()
  .superRefine((l, ctx) => {
    if (Date.parse(l.endsAt) <= Date.parse(l.startsAt)) {
      ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'endsAt must be after startsAt' });
    }
  });
export type SlotLeaseRequestInput = z.input<typeof SlotLeaseRequestSchema>;
export type SlotLeaseRequest = z.output<typeof SlotLeaseRequestSchema>;

/** True when the lease is still in force at `now`. */
export function isLeaseActive(lease: Pick<SlotLease, 'expiresAt'>, now: Date): boolean {
  return Date.parse(lease.expiresAt) > now.getTime();
}

/* ------------------------------------------------------------------------ */
/* Moves, resizes and undo (sched-move 4.2-4.5, M03)                         */
/* ------------------------------------------------------------------------ */

/** Where an appointment should end up. Omitted fields keep their value. */
export const PlacementSchema = z
  .object({
    startsAt: InstantSchema,
    operatoryId: IdSchema,
    /** Changes provider in the same command when dropping into another column. */
    providerId: IdSchema.optional(),
    secondaryProviderId: IdSchema.nullable().optional(),
    /** Resize. Disabled on offices whose capability ignores `lengthMin` (OD v1). */
    lengthMin: LengthMinSchema.optional(),
    /** New pattern; `null` clears it (Undo of a move that added one). */
    timePattern: TimePatternSchema.nullable().optional(),
  })
  .strict();
export type Placement = z.infer<typeof PlacementSchema>;

/** Override of a blocking conflict; needs permission `schedule.override_conflict`. */
export const ConflictOverrideSchema = z
  .object({ reason: z.string().trim().min(3).max(500) })
  .strict();

/** Arrive-early minutes (SB-12): 5-minute steps up to two hours. */
export const ArriveEarlySchema = z.number().int().min(0).max(120).multipleOf(5);

const MoveItemObject = z
  .object({
    appointmentId: IdSchema,
    expectedVersion: RowVersionSchema,
    placement: PlacementSchema,
    /** The caller's lease on the target slot, if it took one. */
    leaseId: IdSchema.optional(),
  })
  .strict();

function refineMoveItem(m: z.infer<typeof MoveItemObject>, ctx: z.RefinementCtx): void {
  checkPatternLength(m.placement, ctx);
  if (m.placement.timePattern !== undefined && m.placement.lengthMin === undefined) {
    ctx.addIssue({
      code: 'custom',
      path: ['placement', 'lengthMin'],
      message: 'a new time pattern must be sent with its length',
    });
  }
}

/**
 * `POST /v1/schedule/appointments/:id/move`. One user gesture, one command
 * (`appointment.update`) and one idempotency key. A stale `expectedVersion`
 * returns 409 with `ScheduleConflictDetails`.
 */
export const MoveRequestSchema = MoveItemObject.extend({
  idempotencyKey: IdempotencyKeySchema,
  override: ConflictOverrideSchema.optional(),
})
  .strict()
  .superRefine(refineMoveItem);
export type MoveRequest = z.infer<typeof MoveRequestSchema>;

/** One appointment inside a compound move. */
export const MoveItemSchema = MoveItemObject.superRefine(refineMoveItem);
export type MoveItem = z.infer<typeof MoveItemSchema>;

/** Multi-select move as one compound command and one undo (sched-move 4.5). */
export const CompoundMoveRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    moves: z.array(MoveItemSchema).min(1).max(50),
    override: ConflictOverrideSchema.optional(),
  })
  .strict()
  .superRefine((r, ctx) => {
    const seen = new Set<string>();
    r.moves.forEach((m, i) => {
      if (seen.has(m.appointmentId)) {
        ctx.addIssue({
          code: 'custom',
          path: ['moves', i, 'appointmentId'],
          message: 'duplicate appointment',
        });
      }
      seen.add(m.appointmentId);
    });
  });
export type CompoundMoveRequest = z.infer<typeof CompoundMoveRequestSchema>;

/** Reasons a placement is refused or warned about (sched-move 4.2). */
export const SCHEDULE_CONFLICT_REASONS = [
  'stale_version',
  'slot_leased',
  'operatory_overlap',
  'provider_overlap',
  'outside_office_hours',
  'provider_not_scheduled',
  'holiday',
  'blockout',
  'capability_unavailable',
  /** Completed visits change operatory on the same day only (SM-18, OD parity). */
  'completed_locked',
  /** Another visit type in a Perfect Day block that is enforced (NEW-WF-03, SV-22/23). */
  'template_mismatch',
] as const;
export const ScheduleConflictReasonSchema = z.enum(SCHEDULE_CONFLICT_REASONS);
export type ScheduleConflictReason = z.infer<typeof ScheduleConflictReasonSchema>;

/**
 * A warning (amber: applied with an Undo toast) or a block (red: snaps back,
 * overridable with a reason where policy allows). Messages contain no patient
 * names; the UI resolves ids to names under the viewer's own permissions.
 */
export const ScheduleIssueSchema = z.object({
  reason: ScheduleConflictReasonSchema,
  severity: z.enum(['warning', 'block']),
  message: z.string().max(300),
  /** Window affected, for the ghost overlay ("Dr A double-booked 10:00-10:20"). */
  startsAt: InstantSchema.nullable(),
  endsAt: InstantSchema.nullable(),
  competingAppointmentId: IdSchema.nullable(),
  leaseHolderName: z.string().max(100).nullable(),
  /** For `stale_version`: who changed it ("Moved by Dana 10 s ago"). */
  changedByName: z.string().max(200).nullable(),
  overridable: z.boolean(),
  /**
   * A warning the office policy wants explained (sched-book SB-06: a third
   * concurrent X). Saving needs `override.reason`, but no special permission;
   * the reason is stored on the appointment history.
   */
  reasonRequired: z.boolean().optional(),
  /**
   * A hard Perfect Day block (`template_mismatch`): the nearest openings that do
   * match the block's visit type, so staff can pick one instead (SV-23).
   */
  alternatives: z
    .array(
      z.object({
        startsAt: InstantSchema,
        endsAt: InstantSchema,
        operatoryId: IdSchema,
        providerId: IdSchema.nullable(),
      }),
    )
    .max(3)
    .optional(),
});
export type ScheduleIssue = z.infer<typeof ScheduleIssueSchema>;

/** `details` of a 409 `conflict` from a schedule write. */
export const ScheduleConflictDetailsSchema = z.object({
  issues: z.array(ScheduleIssueSchema).min(1),
  /** The current row, so the card snaps to the truth. */
  current: AppointmentSchema.nullable(),
  /**
   * A refused booking (the slot was taken, SB-13): the next openings of the
   * same length, chair kind and provider, so the loser can pick one at once.
   */
  alternatives: z.array(z.lazy(() => OpeningSchema)).optional(),
});
export type ScheduleConflictDetails = z.infer<typeof ScheduleConflictDetailsSchema>;

/** 200 body of a move: the optimistic row, its command and the undo. */
export const MoveResultSchema = z.object({
  appointment: AppointmentSchema,
  commandId: IdSchema.nullable(),
  writeBack: WriteBackStateSchema,
  warnings: z.array(ScheduleIssueSchema),
  /** The inverse placement for Undo; sent back with a fresh idempotency key. */
  inverse: z.object({ appointmentId: IdSchema, placement: PlacementSchema }),
  /** Server-side undo entry (`POST /v1/schedule/actions/:id/undo`). */
  actionId: IdSchema.nullable().optional(),
});
export type MoveResult = z.infer<typeof MoveResultSchema>;

/**
 * Appointment fields a placement changes, as command field names, so the API
 * can run `checkCapability(manifest, 'appointment.update', fields)`.
 */
export function placementChangedFields(
  before: Pick<
    Appointment,
    'startsAt' | 'operatoryId' | 'providerId' | 'secondaryProviderId' | 'lengthMin' | 'timePattern'
  >,
  placement: Placement,
): Array<keyof Placement> {
  const out: Array<keyof Placement> = [];
  const startChanged =
    before.startsAt === null || Date.parse(before.startsAt) !== Date.parse(placement.startsAt);
  if (startChanged) out.push('startsAt');
  if (placement.operatoryId !== before.operatoryId) out.push('operatoryId');
  if (placement.providerId !== undefined && placement.providerId !== before.providerId)
    out.push('providerId');
  if (
    placement.secondaryProviderId !== undefined &&
    placement.secondaryProviderId !== before.secondaryProviderId
  )
    out.push('secondaryProviderId');
  if (placement.lengthMin !== undefined && placement.lengthMin !== before.lengthMin)
    out.push('lengthMin');
  if (placement.timePattern !== undefined && placement.timePattern !== before.timePattern)
    out.push('timePattern');
  return out;
}

/** The placement that restores `before` (Undo restores every field in one command). */
export function inversePlacement(
  before: Pick<
    Appointment,
    'startsAt' | 'operatoryId' | 'providerId' | 'secondaryProviderId' | 'lengthMin' | 'timePattern'
  >,
): Placement {
  if (before.startsAt === null || before.operatoryId === null) {
    throw new Error('cannot build an inverse placement for an unplaced appointment');
  }
  // Every resizable field is restored explicitly, including a null pattern,
  // so Undo never leaves a pattern whose length disagrees with lengthMin.
  const p: Placement = {
    startsAt: before.startsAt,
    operatoryId: before.operatoryId,
    secondaryProviderId: before.secondaryProviderId,
    lengthMin: before.lengthMin,
    timePattern: before.timePattern,
  };
  if (before.providerId !== null) p.providerId = before.providerId;
  return p;
}

/* ------------------------------------------------------------------------ */
/* Typed appointment commands (BUILD-PLAN 2.5.3)                             */
/* ------------------------------------------------------------------------ */

/** `changes` of `appointment.update`. At least one field. */
export const AppointmentUpdateChangesSchema = z
  .object({
    startsAt: InstantSchema,
    operatoryId: IdSchema,
    providerId: IdSchema,
    secondaryProviderId: IdSchema.nullable(),
    lengthMin: LengthMinSchema,
    timePattern: TimePatternSchema,
    aptStatus: AptStatusSchema,
    priority: AptPrioritySchema,
    note: z.string().max(4000).nullable(),
  })
  .partial()
  .strict()
  .superRefine((c, ctx) => {
    if (Object.values(c).every((v) => v === undefined)) {
      ctx.addIssue({ code: 'custom', message: 'appointment.update needs at least one change' });
    }
    checkPatternLength(c, ctx);
    if (c.timePattern !== undefined && c.lengthMin === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['lengthMin'],
        message: 'a new time pattern must be sent with its length',
      });
    }
    if (c.aptStatus === OD_APT_STATUS.Broken) {
      ctx.addIssue({
        code: 'custom',
        path: ['aptStatus'],
        message: 'breaking an appointment uses appointment.cancel, which records the reason',
      });
    }
  });
export type AppointmentUpdateChanges = z.infer<typeof AppointmentUpdateChangesSchema>;

/**
 * `changes` of `appointment.cancel`. Per-PMS semantics differ (OD records
 * Broken, Dentrix v1 marks "closed"), so the capability `semanticsNote` is
 * shown in the confirm dialog.
 */
export const AppointmentCancelChangesSchema = z
  .object({
    missedReason: MissedReasonSchema,
    note: z.string().max(4000).optional(),
    /** Velora's finer type; the translator words the PMS reason from it. */
    cancelType: CancelTypeSchema.optional(),
    noticeHours: NoticeHoursSchema.optional(),
  })
  .strict()
  .superRefine((c, ctx) => {
    if (c.cancelType !== undefined && CANCEL_TYPE_MISSED[c.cancelType] !== c.missedReason) {
      ctx.addIssue({
        code: 'custom',
        path: ['cancelType'],
        message: `cancel type ${c.cancelType} is recorded as ${CANCEL_TYPE_MISSED[c.cancelType]}`,
      });
    }
  });
export type AppointmentCancelChanges = z.infer<typeof AppointmentCancelChangesSchema>;

/**
 * `changes` of `appointment.confirm`. Velora is the source of truth for
 * confirmation; the PMS value is resolved per office from its definitions
 * (confirm-remind 4.4).
 */
export const AppointmentConfirmChangesSchema = z
  .object({
    confirmation: z.literal('confirmed'),
    via: ConfirmedViaSchema,
    /** The inbound interaction that confirmed it, for SMS/email replies. */
    interactionId: IdSchema.optional(),
    /**
     * The office's own PMS status code the user picked (Dentrix
     * admin.v_appt_status_codes defid), written to the PMS as that code.
     */
    confirmationPmsDef: z.string().trim().min(1).max(64).optional(),
  })
  .strict()
  .superRefine((c, ctx) => {
    if ((c.via === 'sms' || c.via === 'email') && c.interactionId === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['interactionId'],
        message: 'SMS and email confirmations record the message that confirmed them',
      });
    }
  });
export type AppointmentConfirmChanges = z.infer<typeof AppointmentConfirmChangesSchema>;

export const AppointmentUnconfirmChangesSchema = z
  .object({
    confirmation: ConfirmationStatusSchema.exclude(['confirmed']),
    /**
     * The office's own PMS status code the user picked (Dentrix
     * admin.v_appt_status_codes defid), written to the PMS as that code.
     */
    confirmationPmsDef: z.string().trim().min(1).max(64).optional(),
  })
  .strict();

/**
 * A patient flow step in Velora vocabulary (NEW-WF-06): the step and the
 * server-stamped instants after it. The schedule's translator turns it into
 * the PMS's own instruction (`appointment.flow` in the gateway).
 */
export const AppointmentFlowChangesSchema = z
  .object({
    flowStatus: z.enum(['scheduled', 'here', 'ready', 'chair', 'checkout']),
    arrivedAt: InstantSchema.nullable(),
    seatedAt: InstantSchema.nullable(),
    dismissedAt: InstantSchema.nullable(),
  })
  .strict();
export type AppointmentFlowChanges = z.infer<typeof AppointmentFlowChangesSchema>;

/** `changes` of `appointment.create`. Blocked on OD v1 (BUILD-PLAN 2.5.3). */
export const AppointmentCreateChangesSchema = z
  .object({
    patientId: IdSchema,
    operatoryId: IdSchema,
    providerId: IdSchema,
    secondaryProviderId: IdSchema.nullable().default(null),
    appointmentTypeId: IdSchema.nullable().default(null),
    startsAt: InstantSchema,
    lengthMin: LengthMinSchema,
    timePattern: TimePatternSchema.nullable().default(null),
    priority: AptPrioritySchema.default(OD_APT_PRIORITY.Normal),
    isNewPatient: z.boolean().default(false),
    note: z.string().max(4000).nullable().default(null),
    procedureIds: z.array(IdSchema).max(50).default([]),
    leaseId: IdSchema.optional(),
  })
  .strict()
  .superRefine(checkPatternLength);

const target = (pmsIdRequired: boolean) =>
  z
    .object({
      entity: z.literal('appointment'),
      rowId: pmsIdRequired ? IdSchema : IdSchema.nullable(),
      pmsId: pmsIdRequired ? z.string().min(1).max(64).nullable() : z.null(),
      expectedVersion: z.string().min(1).max(128).nullable(),
    })
    .strict();

/**
 * Appointment commands, discriminated on `type`. Each one is a valid
 * `CommandRequest` from sync.ts with typed `changes`.
 */
export const AppointmentCommandRequestSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('appointment.create'),
      idempotencyKey: IdempotencyKeySchema,
      target: target(false),
      changes: AppointmentCreateChangesSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('appointment.update'),
      idempotencyKey: IdempotencyKeySchema,
      target: target(true),
      changes: AppointmentUpdateChangesSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('appointment.cancel'),
      idempotencyKey: IdempotencyKeySchema,
      target: target(true),
      changes: AppointmentCancelChangesSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('appointment.confirm'),
      idempotencyKey: IdempotencyKeySchema,
      target: target(true),
      changes: AppointmentConfirmChangesSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('appointment.unconfirm'),
      idempotencyKey: IdempotencyKeySchema,
      target: target(true),
      changes: AppointmentUnconfirmChangesSchema,
    })
    .strict(),
]);
export type AppointmentCommandRequest = z.infer<typeof AppointmentCommandRequestSchema>;

/* ------------------------------------------------------------------------ */
/* Range response                                                            */
/* ------------------------------------------------------------------------ */

/** Body of `GET /v1/schedule/appointments`. */
export const ScheduleRangeSchema = z.object({
  locationId: IdSchema,
  /** IANA time zone of the office; all local dates resolve in it. */
  timezone: IanaTimeZoneSchema,
  from: LocalDateSchema,
  to: LocalDateSchema,
  appointments: z.array(AppointmentCardSchema),
  blockouts: z.array(BlockoutSchema),
  leases: z.array(SlotLeaseSchema),
  /** Freshness of the appointments mirror for this office (BUILD-PLAN 2.5.6). */
  freshness: FreshnessSchema,
});
export type ScheduleRange = z.infer<typeof ScheduleRangeSchema>;

/* ======================================================================== */
/* Schedule API (WP1-SCHED-API): `/v1/schedule/*`                            */
/* ======================================================================== */

/**
 * Server-side privacy mode (BUILD-PLAN 2.6): a request carrying this header
 * with the value `1` receives initials only, never patient names, so a
 * waiting-room screen never holds a name in memory.
 */
export const PRIVACY_HEADER = 'x-velora-privacy';

const UUID_TEXT = '[0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12}';
const CSV_IDS_RE = new RegExp(`^${UUID_TEXT}(?:,${UUID_TEXT})*$`);
const BoolParam = z.enum(['true', 'false']);

/** Splits a comma-separated list from a query string. */
export function splitCsv(value: string | undefined): string[] | undefined {
  if (value === undefined || value === '') return undefined;
  return value
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Query-string form of {@link ScheduleQuerySchema} (lists are comma
 * separated, booleans are `true`/`false`). Not strict, so the shell's
 * `office` parameter may ride along.
 */
export const ScheduleQueryParamsSchema = z.object({
  locationId: IdSchema,
  from: LocalDateSchema,
  to: LocalDateSchema,
  operatoryIds: z.string().regex(CSV_IDS_RE).optional(),
  providerIds: z.string().regex(CSV_IDS_RE).optional(),
  appointmentIds: z.string().regex(CSV_IDS_RE).optional(),
  aptStatuses: z
    .string()
    .regex(/^[1235678](?:,[1235678])*$/)
    .optional(),
  includeBlockouts: BoolParam.optional(),
  includeLeases: BoolParam.optional(),
});
export type ScheduleQueryParams = z.infer<typeof ScheduleQueryParamsSchema>;

/** Validates query-string params into a {@link ScheduleQuery}. */
export function parseScheduleQueryParams(p: ScheduleQueryParams) {
  return ScheduleQuerySchema.safeParse({
    locationId: p.locationId,
    from: p.from,
    to: p.to,
    ...(p.operatoryIds ? { operatoryIds: splitCsv(p.operatoryIds) } : {}),
    ...(p.providerIds ? { providerIds: splitCsv(p.providerIds) } : {}),
    ...(p.appointmentIds ? { appointmentIds: splitCsv(p.appointmentIds) } : {}),
    ...(p.aptStatuses ? { aptStatuses: p.aptStatuses.split(',').map(Number) } : {}),
    ...(p.includeBlockouts ? { includeBlockouts: p.includeBlockouts === 'true' } : {}),
    ...(p.includeLeases ? { includeLeases: p.includeLeases === 'true' } : {}),
  });
}

function checkDaySpan(q: { from: string; to: string }, ctx: z.RefinementCtx): void {
  const span = (Date.parse(q.to) - Date.parse(q.from)) / DAY_MS;
  if (span < 0) {
    ctx.addIssue({ code: 'custom', path: ['to'], message: 'to must not be before from' });
  } else if (span + 1 > SCHEDULE_QUERY_MAX_DAYS) {
    ctx.addIssue({
      code: 'custom',
      path: ['to'],
      message: `a schedule query covers at most ${SCHEDULE_QUERY_MAX_DAYS} days`,
    });
  }
}

/* ------------------------------------------------------------------------ */
/* Conflict policy (per office; sched-book 4.4, BUILD-PLAN 3.5)              */
/* ------------------------------------------------------------------------ */

/** `allow` raises nothing, `warn` applies with a warning, `block` refuses (overridable). */
export const CONFLICT_POLICY_LEVELS = ['allow', 'warn', 'block'] as const;
export const ConflictPolicyLevelSchema = z.enum(CONFLICT_POLICY_LEVELS);
export type ConflictPolicyLevel = z.infer<typeof ConflictPolicyLevelSchema>;

const PolicyFields = {
  /** Snap increment (OD Pattern units are 5 minutes; BUILD-PLAN 3.5). */
  incrementMin: z.union([z.literal(5), z.literal(10), z.literal(15)]),
  /** Grid start and end, local minutes from midnight (default 07:00-18:00). */
  dayStartMinute: z.number().int().min(0).max(1435),
  dayEndMinute: z.number().int().min(5).max(1440),
  operatoryOverlap: ConflictPolicyLevelSchema,
  /** Provider conflict, judged only on overlapping X segments. */
  providerOverlap: ConflictPolicyLevelSchema,
  /** A third (or more) concurrent X for one provider; `warn` asks for a reason (SB-06). */
  tripleProviderOverlap: ConflictPolicyLevelSchema,
  outsideOfficeHours: ConflictPolicyLevelSchema,
  providerNotScheduled: ConflictPolicyLevelSchema,
  holiday: ConflictPolicyLevelSchema,
  blockout: ConflictPolicyLevelSchema,
  /** A pinboard HOLD older than this shows "stale hold" (sched-move 4.1). */
  staleHoldHours: z.number().int().min(1).max(720),
} as const;

export const SchedulePolicySchema = z
  .object({ locationId: IdSchema, ...PolicyFields, updatedAt: InstantSchema.nullable() })
  .superRefine((p, ctx) => {
    if (p.dayEndMinute <= p.dayStartMinute) {
      ctx.addIssue({
        code: 'custom',
        path: ['dayEndMinute'],
        message: 'day end is after day start',
      });
    }
  });
export type SchedulePolicy = z.infer<typeof SchedulePolicySchema>;
export type SchedulePolicyRules = Omit<SchedulePolicy, 'locationId' | 'updatedAt'>;

/** Office defaults when nothing is configured. */
export const DEFAULT_SCHEDULE_POLICY: Readonly<SchedulePolicyRules> = Object.freeze({
  incrementMin: 10,
  dayStartMinute: 7 * 60,
  dayEndMinute: 18 * 60,
  operatoryOverlap: 'block',
  providerOverlap: 'warn',
  tripleProviderOverlap: 'warn',
  outsideOfficeHours: 'warn',
  providerNotScheduled: 'warn',
  holiday: 'block',
  blockout: 'warn',
  staleHoldHours: 24,
});

/** `PUT /v1/schedule/policy`: change some fields of the office policy. */
export const SchedulePolicyPatchSchema = z
  .object({
    locationId: IdSchema,
    incrementMin: PolicyFields.incrementMin.optional(),
    dayStartMinute: PolicyFields.dayStartMinute.optional(),
    dayEndMinute: PolicyFields.dayEndMinute.optional(),
    operatoryOverlap: ConflictPolicyLevelSchema.optional(),
    providerOverlap: ConflictPolicyLevelSchema.optional(),
    tripleProviderOverlap: ConflictPolicyLevelSchema.optional(),
    outsideOfficeHours: ConflictPolicyLevelSchema.optional(),
    providerNotScheduled: ConflictPolicyLevelSchema.optional(),
    holiday: ConflictPolicyLevelSchema.optional(),
    blockout: ConflictPolicyLevelSchema.optional(),
    staleHoldHours: PolicyFields.staleHoldHours.optional(),
  })
  .strict()
  .superRefine((p, ctx) => {
    if (Object.values(p).filter((v) => v !== undefined).length < 2) {
      ctx.addIssue({ code: 'custom', message: 'change at least one policy field' });
    }
  });
export type SchedulePolicyPatch = z.infer<typeof SchedulePolicyPatchSchema>;

/* ------------------------------------------------------------------------ */
/* Capabilities as the schedule shows them                                   */
/* ------------------------------------------------------------------------ */

/** Schedule actions whose availability depends on the office's PMS. */
export const SCHEDULE_ACTIONS = [
  'create',
  'move',
  'resize',
  'change_provider',
  'confirm',
  'set_confirmation',
  'break',
  'complete',
  'edit_note',
  'release',
] as const;
export const ScheduleActionNameSchema = z.enum(SCHEDULE_ACTIONS);
export type ScheduleActionName = z.infer<typeof ScheduleActionNameSchema>;

/**
 * Whether a control is enabled; when not, `reason` is the tooltip. When
 * enabled, `writesToPms` says a command goes to the outbox and `semantics`
 * is what the PMS actually does (shown in confirm dialogs).
 */
export const ActionAvailabilitySchema = z.object({
  enabled: z.boolean(),
  writesToPms: z.boolean(),
  reason: z.string().max(300).nullable(),
  semantics: z.string().max(300).nullable(),
  /**
   * The PMS owns appointments here and its gate (WRITE_READY, the capability
   * manifest or a blocked field) refuses this change, but writes are not
   * paused: the UI keeps the control and hands the exact change to staff to
   * make in the PMS ("Do this in Dentrix", a task). Never set where the
   * change is written; `enabled` stays false, so nothing is queued.
   */
  handoff: z.boolean().optional(),
});
export type ActionAvailability = z.infer<typeof ActionAvailabilitySchema>;

/** Booking fields a new appointment can carry beyond patient, chair, provider and time. */
export const BOOKING_EXTRA_FIELDS = [
  'hygienist',
  'timePattern',
  'appointmentType',
  'asap',
  'isNewPatient',
  'procedures',
  'note',
] as const;
export const BookingExtraFieldSchema = z.enum(BOOKING_EXTRA_FIELDS);
export type BookingExtraField = z.infer<typeof BookingExtraFieldSchema>;

export const CreateFieldReachSchema = z.object({
  field: BookingExtraFieldSchema,
  label: z.string().max(60),
  reaches: z.boolean(),
  reason: z.string().max(300).nullable(),
});
export type CreateFieldReach = z.infer<typeof CreateFieldReachSchema>;

export const ScheduleCapabilitiesSchema = z.object({
  pmsSystem: PmsSystemSchema.nullable(),
  adapter: PmsAdapterSchema.nullable(),
  /** Who owns appointments at this office now (SoR stage, BUILD-PLAN 2.5.5). */
  systemOfRecord: z.enum(['pms', 'velora']),
  writesPaused: z.boolean(),
  writesPausedReason: z.string().max(300).nullable(),
  actions: z.record(ScheduleActionNameSchema, ActionAvailabilitySchema),
  /**
   * WF-13: per booking field beyond patient, chair, provider and time,
   * whether a new booking's value reaches the PMS on this office's adapter
   * (derived from the gateway create schema and the adapter's mapping).
   * Absent or empty where appointments are Velora's own.
   */
  createFieldReach: z.array(CreateFieldReachSchema).optional(),
});
export type ScheduleCapabilities = z.infer<typeof ScheduleCapabilitiesSchema>;

/* ------------------------------------------------------------------------ */
/* PMS hand-off: "Do this in Dentrix"                                         */
/* ------------------------------------------------------------------------ */

/** The schedule change staff are asked to make in the PMS. */
export const PMS_HANDOFF_ACTIONS = [
  'book',
  'move',
  'resize',
  'break',
  'edit_note',
  'change_provider',
  'status',
  'edit',
] as const;
export const PmsHandoffActionSchema = z.enum(PMS_HANDOFF_ACTIONS);
export type PmsHandoffAction = z.infer<typeof PmsHandoffActionSchema>;

/**
 * `POST /v1/schedule/pms-handoffs`: a refused schedule change becomes a task
 * (the house "Add in <PMS>" pattern). The title names the patient from the
 * server's own record; `lines` say exactly what to enter in the PMS and carry
 * no patient name. One open task per appointment and change.
 */
export const PmsHandoffRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    locationId: IdSchema,
    action: PmsHandoffActionSchema,
    appointmentId: IdSchema.nullable().default(null),
    patientId: IdSchema.nullable().default(null),
    lines: z.array(z.string().min(1).max(300)).min(1).max(12),
    /** Why Velora could not write it (the gate reason shown in the dialog). */
    reason: z.string().max(300).nullable().default(null),
  })
  .strict();
export type PmsHandoffRequestInput = z.input<typeof PmsHandoffRequestSchema>;
export type PmsHandoffRequest = z.output<typeof PmsHandoffRequestSchema>;

export const PmsHandoffResultSchema = z.object({
  taskId: IdSchema,
  title: z.string().max(300),
  /** False when an open task for the same change already existed. */
  created: z.boolean(),
});
export type PmsHandoffResult = z.infer<typeof PmsHandoffResultSchema>;

/* ------------------------------------------------------------------------ */
/* Saved views (Dentrix "Views"): as many as the office wants                 */
/* ------------------------------------------------------------------------ */

export const SavedViewKindSchema = z.enum(['all', 'doctors', 'hygiene']);

/** What a saved view shows. Null lists mean every column of that kind. */
export const ScheduleSavedViewConfigSchema = z
  .object({
    kind: SavedViewKindSchema.default('all'),
    columns: z.enum(['operatory', 'provider']).default('operatory'),
    operatoryIds: z.array(IdSchema).max(200).nullable().default(null),
    providerIds: z.array(IdSchema).max(200).nullable().default(null),
  })
  .strict();
export type ScheduleSavedViewConfig = z.output<typeof ScheduleSavedViewConfigSchema>;

export const ScheduleSavedViewSchema = z.object({
  id: IdSchema,
  name: z.string().min(1).max(60),
  /** Shared with the office (null owner) or the caller's own. */
  shared: z.boolean(),
  mine: z.boolean(),
  sortOrder: z.number().int(),
  config: ScheduleSavedViewConfigSchema,
  version: RowVersionSchema,
});
export type ScheduleSavedView = z.infer<typeof ScheduleSavedViewSchema>;

/** `GET /v1/schedule/saved-views?locationId=`. */
export const SavedViewsQuerySchema = z.object({ locationId: IdSchema });
export const SavedViewsResultSchema = z.object({ views: z.array(ScheduleSavedViewSchema) });
export type SavedViewsResult = z.infer<typeof SavedViewsResultSchema>;

/** `POST /v1/schedule/saved-views`. Shared views need schedule.settings. */
export const SavedViewCreateSchema = z
  .object({
    locationId: IdSchema,
    name: z.string().trim().min(1).max(60),
    shared: z.boolean().default(false),
    config: ScheduleSavedViewConfigSchema,
  })
  .strict();
export type SavedViewCreateInput = z.input<typeof SavedViewCreateSchema>;

/** `PATCH /v1/schedule/saved-views/:id`. */
export const SavedViewPatchSchema = z
  .object({
    expectedVersion: RowVersionSchema,
    name: z.string().trim().min(1).max(60).optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    config: ScheduleSavedViewConfigSchema.optional(),
  })
  .strict();
export type SavedViewPatchInput = z.input<typeof SavedViewPatchSchema>;

/* ------------------------------------------------------------------------ */
/* Office context for a date range: columns, rules, notes, header numbers    */
/* ------------------------------------------------------------------------ */

/** `GET /v1/schedule/context`. */
export const ScheduleContextQuerySchema = z
  .object({ locationId: IdSchema, from: LocalDateSchema, to: LocalDateSchema })
  .superRefine(checkDaySpan);
export type ScheduleContextQuery = z.infer<typeof ScheduleContextQuerySchema>;

const LocalMinute = z.number().int().min(0).max(1440);

export const ScheduleOperatorySchema = z.object({
  id: IdSchema,
  name: z.string().min(1).max(100),
  abbr: z.string().min(1).max(12),
  isHygiene: z.boolean(),
  isHidden: z.boolean(),
  onlineBookable: z.boolean(),
  sortOrder: z.number().int(),
  defaultProviderId: IdSchema.nullable(),
  defaultHygienistId: IdSchema.nullable(),
});
export type ScheduleOperatory = z.infer<typeof ScheduleOperatorySchema>;

export const ScheduleProviderSchema = z.object({
  id: IdSchema,
  abbr: z.string().min(1).max(12),
  displayName: z.string().min(1).max(200),
  providerType: z.enum(['dentist', 'hygienist', 'assistant', 'other']),
  /** Design token `provider-1`..`provider-24`, never a raw colour. */
  colorToken: z.string().regex(/^provider-([1-9]|1\d|2[0-4])$/),
  isHidden: z.boolean(),
  /**
   * Offered in booking and provider pickers (S6, PO-05): a visible dentist
   * or hygienist. Non-clinical rows (assistants, front desk logins,
   * placeholders) are listed only so cards booked to them resolve a name.
   */
  bookable: z.boolean().optional(),
});
export type ScheduleProvider = z.infer<typeof ScheduleProviderSchema>;

export const ScheduleAppointmentTypeSchema = z.object({
  id: IdSchema,
  name: z.string().min(1).max(100),
  colorToken: z.string().regex(/^appt-([1-9]|[1-3]\d|40)$/),
  defaultLengthMin: LengthMinSchema,
  timePattern: TimePatternSchema.nullable(),
  procedureCodes: z.array(z.string().min(1).max(15)),
  onlineBookable: z.boolean(),
});
export type ScheduleAppointmentType = z.infer<typeof ScheduleAppointmentTypeSchema>;

export const MinuteWindowSchema = z.object({ startMinute: LocalMinute, endMinute: LocalMinute });
export type MinuteWindow = z.infer<typeof MinuteWindowSchema>;

/** A provider working in an operatory (or anywhere, `operatoryId` null) that day. */
export const StaffingWindowSchema = z.object({
  operatoryId: IdSchema.nullable(),
  providerId: IdSchema,
  startMinute: LocalMinute,
  endMinute: LocalMinute,
});
export type StaffingWindow = z.infer<typeof StaffingWindowSchema>;

/**
 * Who works a chair when the office keeps no provider schedules that day and
 * the chair has no default provider: inferred nightly from the office's own
 * book of the last 12 weeks (show as "inferred from recent schedule"). The
 * office overrides it per chair with the chair's default provider (Settings,
 * Operatories). Office hours still decide the open time.
 */
export const ChairDefaultWindowSchema = z.object({
  operatoryId: IdSchema,
  providerId: IdSchema,
  startMinute: LocalMinute,
  endMinute: LocalMinute,
  source: z.literal('inferred'),
  confidence: z.enum(['high', 'medium', 'low']),
});
export type ChairDefaultWindow = z.infer<typeof ChairDefaultWindowSchema>;

/** PTO, extra hours or a closure (`schedule_exception`); null minutes mean all day. */
export const TimeOffSchema = z.object({
  providerId: IdSchema.nullable(),
  kind: z.enum(['pto', 'extra_hours', 'office_closed', 'other']),
  startMinute: LocalMinute.nullable(),
  endMinute: LocalMinute.nullable(),
  note: z.string().max(500).nullable(),
});
export type TimeOff = z.infer<typeof TimeOffSchema>;

/** Perfect Day template block for that weekday. */
export const TemplateBlockSchema = z.object({
  id: IdSchema,
  operatoryId: IdSchema,
  startMinute: LocalMinute,
  lengthMin: LengthMinSchema,
  appointmentTypeId: IdSchema.nullable(),
  appointmentTypeName: z.string().max(100).nullable(),
  onlineBookable: z.boolean(),
  /** guide (drawn only), warn (another type needs a reason), hard (refused); NEW-WF-03. */
  enforcement: z.enum(['guide', 'warn', 'hard']).optional(),
  /**
   * Released (Levin): still empty inside its release window, so it no longer
   * reserves its time and `enforcement` reads `guide`. Absent when the block
   * has no release window or is not released.
   */
  released: z.boolean().optional(),
  /** The block's visit-type colour token, for the watermark. */
  colorToken: z
    .string()
    .regex(/^appt-([1-9]|[1-3]\d|40)$/)
    .nullable()
    .optional(),
});
export type TemplateBlock = z.infer<typeof TemplateBlockSchema>;

/** Day note: one per office per day, or per operatory per day. */
export const DayNoteSchema = z.object({
  id: IdSchema,
  ...LocationOwnedSchema.shape,
  day: LocalDateSchema,
  operatoryId: IdSchema.nullable(),
  body: z.string().min(1).max(4000),
  updatedAt: InstantSchema,
  updatedByName: z.string().max(200).nullable(),
  version: RowVersionSchema,
});
export type DayNote = z.infer<typeof DayNoteSchema>;

/**
 * Column header numbers. Money is null without `money.view` (the server
 * strips it, SV-16). `fillRatio` is booked minutes over open minutes, null
 * when the column has no open time.
 */
export const ColumnStatSchema = z.object({
  operatoryId: IdSchema,
  /** Provider scheduled in this operatory that day (first window), else the op default. */
  providerId: IdSchema.nullable(),
  appointmentCount: z.number().int().nonnegative(),
  bookedMin: z.number().int().nonnegative(),
  capacityMin: z.number().int().nonnegative(),
  fillRatio: z.number().nonnegative().nullable(),
  scheduledCents: z.number().int().nullable(),
  completedCents: z.number().int().nullable(),
});
export type ColumnStat = z.infer<typeof ColumnStatSchema>;

export const DayTotalsSchema = z.object({
  appointmentCount: z.number().int().nonnegative(),
  unconfirmedCount: z.number().int().nonnegative(),
  newPatientCount: z.number().int().nonnegative(),
  brokenCount: z.number().int().nonnegative(),
  bookedMin: z.number().int().nonnegative(),
  capacityMin: z.number().int().nonnegative(),
  /** Ratio of sums over the columns, never an average of ratios. */
  fillRatio: z.number().nonnegative().nullable(),
  scheduledCents: z.number().int().nullable(),
  /** Part of `scheduledCents` estimated from appointment-type defaults (SV-18 "est."). */
  estimatedCents: z.number().int().nullable(),
  completedCents: z.number().int().nullable(),
});
export type DayTotals = z.infer<typeof DayTotalsSchema>;

export const ScheduleDaySchema = z.object({
  date: LocalDateSchema,
  weekday: z.number().int().min(0).max(6),
  closed: z.boolean(),
  /** Holiday name or "Office closed"; null when open. */
  closedReason: z.string().max(200).nullable(),
  officeHours: z.array(MinuteWindowSchema),
  staffing: z.array(StaffingWindowSchema),
  /** Inferred chair providers; only on days with no provider schedules (ChairDefaultWindowSchema). */
  chairDefaults: z.array(ChairDefaultWindowSchema).max(2000).optional(),
  timeOff: z.array(TimeOffSchema),
  templateBlocks: z.array(TemplateBlockSchema),
  dayNotes: z.array(DayNoteSchema),
  columns: z.array(ColumnStatSchema),
  totals: DayTotalsSchema,
  /**
   * Production goal per provider that day, prorated for time off (SV-12, SV-15).
   * Null without money.view; absent or empty when no goals are set.
   */
  providerGoals: z
    .array(z.object({ providerId: IdSchema, goalCents: z.number().int().nonnegative() }))
    .nullable()
    .optional(),
});
export type ScheduleDay = z.infer<typeof ScheduleDaySchema>;

/** "was: ..." ghost of a RELEASED cut for 10 minutes (sched-move 4.1). */
export const RELEASED_GHOST_MINUTES = 10;
export const ReleasedGhostSchema = z.object({
  appointmentId: IdSchema,
  pinboardItemId: IdSchema,
  operatoryId: IdSchema.nullable(),
  startsAt: InstantSchema,
  endsAt: InstantSchema,
  patient: AppointmentPatientRefSchema.nullable(),
  releasedAt: InstantSchema,
  expiresAt: InstantSchema,
});
export type ReleasedGhost = z.infer<typeof ReleasedGhostSchema>;

/**
 * How booking works at this office (OD-06 banner): `velora` (Velora is the
 * schedule), `writes_to_pms` (bookings are written to the PMS), `pms_only`
 * (book in the PMS; it shows here after sync), `writes_paused`.
 */
export const ScheduleBookingModeSchema = z.enum([
  'velora',
  'writes_to_pms',
  'pms_only',
  'writes_paused',
]);
export type ScheduleBookingMode = z.infer<typeof ScheduleBookingModeSchema>;

/** Where office hours come from (S2, PO-06): none set, entered in Velora, or synced from the PMS. */
export const HoursSourceSchema = z.enum(['none', 'velora', 'pms']);
export type HoursSource = z.infer<typeof HoursSourceSchema>;

export const ScheduleMeSchema = z.object({
  /** The provider record linked to the user; null for staff who are not providers. */
  providerId: IdSchema.nullable(),
  providerType: z.enum(['dentist', 'hygienist', 'assistant', 'other']).nullable(),
  /** schedule.edit at this office. */
  canEdit: z.boolean(),
  /** schedule.edit_others at this office. */
  editOthers: z.boolean(),
});
export type ScheduleMe = z.infer<typeof ScheduleMeSchema>;

export const ScheduleContextSchema = z.object({
  locationId: IdSchema,
  timezone: IanaTimeZoneSchema,
  from: LocalDateSchema,
  to: LocalDateSchema,
  /** Office-local "today", for flow rules and the now-line. */
  today: LocalDateSchema,
  serverTime: InstantSchema,
  moneyVisible: z.boolean(),
  policy: SchedulePolicySchema,
  capabilities: ScheduleCapabilitiesSchema,
  operatories: z.array(ScheduleOperatorySchema),
  providers: z.array(ScheduleProviderSchema),
  appointmentTypes: z.array(ScheduleAppointmentTypeSchema),
  days: z.array(ScheduleDaySchema),
  ghosts: z.array(ReleasedGhostSchema),
  /** Booking banner state (always sent by the API; optional for older clients). */
  bookingMode: ScheduleBookingModeSchema.optional(),
  bookingModeReason: z.string().max(300).nullable().optional(),
  /** `none`: no office hours, so openings and capacity are empty for that reason. */
  hoursSource: HoursSourceSchema.optional(),
  /**
   * False when this office's PMS feed does not link procedures to
   * appointments (Dentrix over PulseSync v1, S5/F6): the production numbers
   * are then null ("not available"), never $0.
   */
  productionAvailable: z.boolean().optional(),
  /**
   * The office's appointment statuses (Dentrix Practice Definitions >
   * Appointment Status): the left status bar, its tooltip and the Legend.
   * Absent from older servers: Velora's defaults apply.
   */
  apptStatuses: z.array(ApptStatusDefSchema).optional(),
  /**
   * The signed-in user as the schedule sees them: their provider record and
   * whether they may book other providers' columns. The booking form uses it
   * to explain a refusal before anything is filled in (hygienist role test,
   * finding 13) and to put a hygienist on her own visits by default.
   */
  me: ScheduleMeSchema.optional(),
});
export type ScheduleContext = z.infer<typeof ScheduleContextSchema>;

/* ------------------------------------------------------------------------ */
/* Multi-office summary ("All offices": per office plus combined)            */
/* ------------------------------------------------------------------------ */

export const ScheduleSummaryQuerySchema = z
  .object({ from: LocalDateSchema, to: LocalDateSchema })
  .superRefine(checkDaySpan);

export const SummaryNumbersSchema = z.object({
  appointmentCount: z.number().int().nonnegative(),
  unconfirmedCount: z.number().int().nonnegative(),
  brokenCount: z.number().int().nonnegative(),
  bookedMin: z.number().int().nonnegative(),
  capacityMin: z.number().int().nonnegative(),
  fillRatio: z.number().nonnegative().nullable(),
  scheduledCents: z.number().int().nullable(),
});
export type SummaryNumbers = z.infer<typeof SummaryNumbersSchema>;

export const SummaryDaySchema = SummaryNumbersSchema.extend({ date: LocalDateSchema });
export type SummaryDay = z.infer<typeof SummaryDaySchema>;

export const OfficeScheduleSummarySchema = z.object({
  locationId: IdSchema,
  name: z.string(),
  shortName: z.string(),
  timezone: IanaTimeZoneSchema,
  days: z.array(SummaryDaySchema),
  totals: SummaryNumbersSchema,
});
export type OfficeScheduleSummary = z.infer<typeof OfficeScheduleSummarySchema>;

/**
 * `GET /v1/schedule/summary` (office scope `location-or-all`): one entry per
 * office in scope and a combined rollup. Combined money is null unless the
 * viewer may see money at every office in scope; combined ratios are ratios
 * of summed minutes, never averages of ratios.
 */
export const ScheduleSummarySchema = z.object({
  from: LocalDateSchema,
  to: LocalDateSchema,
  offices: z.array(OfficeScheduleSummarySchema),
  combined: z.object({ days: z.array(SummaryDaySchema), totals: SummaryNumbersSchema }),
});
export type ScheduleSummary = z.infer<typeof ScheduleSummarySchema>;

/* ------------------------------------------------------------------------ */
/* Write results                                                             */
/* ------------------------------------------------------------------------ */

/**
 * What happened on the PMS side of a write:
 * - `queued`: a command is in the outbox (watch `writeBack`).
 * - `not_required`: Velora is the system of record here; nothing to write.
 * - `skipped`: Velora recorded it, but this PMS cannot take it (`reason`).
 * - `deferred`: kept in Velora until placed (a cut on a read-mostly PMS).
 */
export const PmsWriteOutcomeSchema = z.object({
  status: z.enum(['queued', 'not_required', 'skipped', 'deferred']),
  reason: z.string().max(300).nullable(),
});
export type PmsWriteOutcome = z.infer<typeof PmsWriteOutcomeSchema>;

export const ScheduleWriteResultSchema = z.object({
  appointment: AppointmentSchema,
  commandId: IdSchema.nullable(),
  writeBack: WriteBackStateSchema,
  warnings: z.array(ScheduleIssueSchema),
  actionId: IdSchema.nullable(),
  pmsWrite: PmsWriteOutcomeSchema,
});
export type ScheduleWriteResult = z.infer<typeof ScheduleWriteResultSchema>;

/**
 * `POST /v1/schedule/appointments/:id/transfer` (scheduler role test
 * 2026-10-20, fix 2): move a booked appointment to another office of the
 * practice, keeping the same appointment (patient, procedures, note). The
 * request runs in the source office's scope; the user needs schedule.edit
 * in both offices. Where either office's PMS owns appointments, the server
 * refuses with 422 `capability_unavailable` (reason `cross_office_pms`) and
 * the UI hands the change off ("Break at A, book at B in Dentrix") before
 * sending anything.
 */
export const TransferRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    expectedVersion: RowVersionSchema,
    toLocationId: IdSchema,
    placement: z
      .object({
        startsAt: InstantSchema,
        operatoryId: IdSchema,
        providerId: IdSchema,
        secondaryProviderId: IdSchema.nullable().optional(),
      })
      .strict(),
    override: ConflictOverrideSchema.optional(),
  })
  .strict();
export type TransferRequest = z.infer<typeof TransferRequestSchema>;

export const TransferResultSchema = z.object({
  appointment: AppointmentSchema,
  fromLocationId: IdSchema,
  toLocationId: IdSchema,
  warnings: z.array(ScheduleIssueSchema),
});
export type TransferResult = z.infer<typeof TransferResultSchema>;

export const CompoundMoveResultSchema = z.object({
  results: z.array(MoveResultSchema),
  actionId: IdSchema.nullable(),
});
export type CompoundMoveResult = z.infer<typeof CompoundMoveResultSchema>;

/* ------------------------------------------------------------------------ */
/* Create, edit, status, break                                               */
/* ------------------------------------------------------------------------ */

/**
 * What to do with a procedure that already sits on another live upcoming
 * appointment (SB-08 "Keep / Move / Back", NEW-WF-01):
 * - `ask` (default): refuse with 409 `conflict`, details
 *   `{ kind: 'procedure_conflict', conflicts }`, so the panel shows the dialog;
 * - `move`: take it off the other appointment and attach it here;
 * - `keep`: leave those where they are and attach the rest.
 * "Back" is the client closing the dialog without a second request.
 */
export const ProcedureConflictChoiceSchema = z.enum(['ask', 'move', 'keep']);
export type ProcedureConflictChoice = z.infer<typeof ProcedureConflictChoiceSchema>;

/** `POST /v1/schedule/appointments`. Blocked on OD v1 (BUILD-PLAN 2.5.3). */
export const CreateAppointmentRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    locationId: IdSchema,
    patientId: IdSchema,
    operatoryId: IdSchema,
    providerId: IdSchema,
    secondaryProviderId: IdSchema.nullable().default(null),
    appointmentTypeId: IdSchema.nullable().default(null),
    startsAt: InstantSchema,
    lengthMin: LengthMinSchema,
    timePattern: TimePatternSchema.nullable().default(null),
    priority: AptPrioritySchema.default(OD_APT_PRIORITY.Normal),
    isNewPatient: z.boolean().default(false),
    note: z.string().max(4000).nullable().default(null),
    /** Planned procedures of this patient to attach (TP / TPi rows, never conditions). */
    procedureIds: z.array(IdSchema).max(50).default([]),
    /**
     * The assistant on the `/` segments (Dentrix / OD "Assistant", WF-14): a
     * provider row of type `assistant`. Kept in Velora; no PMS command carries it.
     */
    assistantId: IdSchema.nullable().default(null),
    /**
     * Ask the patient to arrive this many minutes early (OD
     * DateTimeAskedToArrive, SB-12). The arrival time must fall inside office
     * hours; the server refuses it otherwise.
     */
    arriveEarlyMin: ArriveEarlySchema.nullable().default(null),
    /**
     * Confirmation set at booking (WF-04). Only where the visit is Velora's
     * own record; at a PMS office the visit is confirmed once the PMS has it
     * (the server refuses it with that reason).
     */
    confirmation: ConfirmationStatusSchema.default('unconfirmed'),
    /** A procedure already on another live upcoming appointment (SB-08). */
    onConflict: ProcedureConflictChoiceSchema.default('ask'),
    /** The caller's lease on the slot (taken when the panel opened). */
    leaseId: IdSchema.optional(),
    override: ConflictOverrideSchema.optional(),
    /**
     * Cross-office patients (2026-11-01): book a patient of another office
     * here ("Book this <home> patient at <office>"). Where this office keeps
     * its own PMS, Velora uses the person's record there, links a strong
     * match, or adds them to that PMS first (the booking then answers 409
     * `cross_office_pending` until the new record has synced).
     */
    crossOffice: z.boolean().optional(),
  })
  .strict()
  .superRefine(checkPatternLength);
export type CreateAppointmentRequestInput = z.input<typeof CreateAppointmentRequestSchema>;
export type CreateAppointmentRequest = z.output<typeof CreateAppointmentRequestSchema>;

const PATCH_FIELDS = [
  'note',
  'priority',
  'appointmentTypeId',
  'isNewPatient',
  'providerId',
  'secondaryProviderId',
  'assistantId',
  'arriveEarlyMin',
] as const;

/** `PATCH /v1/schedule/appointments/:id`: fields that are not a placement. */
export const AppointmentPatchRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    expectedVersion: RowVersionSchema,
    note: z.string().max(4000).nullable().optional(),
    priority: AptPrioritySchema.optional(),
    appointmentTypeId: IdSchema.nullable().optional(),
    isNewPatient: z.boolean().optional(),
    providerId: IdSchema.optional(),
    secondaryProviderId: IdSchema.nullable().optional(),
    assistantId: IdSchema.nullable().optional(),
    arriveEarlyMin: ArriveEarlySchema.nullable().optional(),
    override: ConflictOverrideSchema.optional(),
  })
  .strict()
  .superRefine((r, ctx) => {
    if (PATCH_FIELDS.every((f) => r[f] === undefined)) {
      ctx.addIssue({ code: 'custom', message: 'change at least one field' });
    }
  });
export type AppointmentPatchRequest = z.infer<typeof AppointmentPatchRequestSchema>;

/**
 * `POST /v1/schedule/appointments/:id/status`: exactly one of a flow step
 * (on the appointment's own day, kept in Velora) or a confirmation (Velora
 * is the source of truth; written to the PMS where the office allows it).
 */
export const StatusChangeRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    expectedVersion: RowVersionSchema,
    flowStatus: FlowStatusSchema.optional(),
    confirmation: ConfirmationStatusSchema.optional(),
    /** How a confirmation was obtained; default `staff`. */
    via: ConfirmedViaSchema.optional(),
    interactionId: IdSchema.optional(),
    /**
     * The PMS's own status the user picked (Dentrix `LMM`, `MULTI`, ...; OD
     * DefNum), kept raw on the appointment so it is shown and written back
     * exactly (S17). Only with `confirmation`.
     */
    confirmationPmsDef: z.string().trim().min(1).max(64).optional(),
    /**
     * Where a flow step was pressed (NEW-WF-06, SF-01): the desk board, the
     * chair (operatory device), the Light Board. Stored on the event; only
     * with `flowStatus`. Default `desk`.
     */
    source: z.enum(['desk', 'chair', 'lightboard']).optional(),
  })
  .strict()
  .superRefine((r, ctx) => {
    if (r.source !== undefined && r.flowStatus === undefined) {
      ctx.addIssue({ code: 'custom', path: ['source'], message: 'source goes with flowStatus' });
    }
    if ((r.flowStatus === undefined) === (r.confirmation === undefined)) {
      ctx.addIssue({ code: 'custom', message: 'send exactly one of flowStatus or confirmation' });
    }
    if (r.confirmationPmsDef !== undefined && r.confirmation === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['confirmationPmsDef'],
        message: 'confirmationPmsDef is only sent with confirmation',
      });
    }
    if (r.via !== undefined && r.confirmation !== 'confirmed') {
      ctx.addIssue({ code: 'custom', path: ['via'], message: 'via is only sent with confirmed' });
    }
    if ((r.via === 'sms' || r.via === 'email') && r.interactionId === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['interactionId'],
        message: 'SMS and email confirmations record the message that confirmed them',
      });
    }
  });
export type StatusChangeRequest = z.infer<typeof StatusChangeRequestSchema>;

/**
 * `POST /v1/schedule/appointments/:id/break`. Send a `cancelType`
 * (cancelled by patient with notice, late cancel, no-show, cancelled by
 * office) or, as before, a plain `missedReason`; both together must agree.
 */
export const BreakRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    expectedVersion: RowVersionSchema,
    missedReason: MissedReasonSchema.optional(),
    cancelType: CancelTypeSchema.optional(),
    /** Hours of notice the patient gave. */
    noticeHours: NoticeHoursSchema.optional(),
    /** D9986 / D9987 or an office code; recorded for posting by Billing (NEW-WF-10). */
    feeCode: CancelFeeCodeSchema.optional(),
    note: z.string().trim().max(500).optional(),
    /** `pinboard`: also put it on my pinboard (released) for rebooking. */
    then: z.enum(['none', 'pinboard']).default('none'),
    /**
     * Left without being seen (NEW-WF-06, WF-05): a no-show of a patient who
     * had arrived. The arrival stamp is kept and the visit counts like a
     * no-show. Only with `missedReason: 'no_show'`.
     */
    leftWithoutBeingSeen: z.boolean().optional(),
  })
  .strict()
  .superRefine((r, ctx) => {
    if (r.missedReason === undefined && r.cancelType === undefined) {
      ctx.addIssue({ code: 'custom', message: 'send a cancelType or a missedReason' });
    }
    if (
      r.missedReason !== undefined &&
      r.cancelType !== undefined &&
      CANCEL_TYPE_MISSED[r.cancelType] !== r.missedReason
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['cancelType'],
        message: 'the cancel type does not match the missed reason',
      });
    }
    // NEW-WF-06: left without being seen is recorded as a no-show.
    if (
      r.leftWithoutBeingSeen &&
      (r.cancelType ?? MISSED_CANCEL_TYPE[r.missedReason ?? 'broken']) !== 'no_show'
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['leftWithoutBeingSeen'],
        message: 'left without being seen is recorded as a no-show',
      });
    }
    if (r.feeCode !== undefined && (r.cancelType ?? r.missedReason) === 'cancelled_by_office') {
      ctx.addIssue({
        code: 'custom',
        path: ['feeCode'],
        message: 'an office cancellation carries no patient fee',
      });
    }
  });

/** The cancel type a break request stands for. */
export function cancelTypeOf(r: Pick<BreakRequest, 'missedReason' | 'cancelType'>): CancelType {
  if (r.cancelType) return r.cancelType;
  return MISSED_CANCEL_TYPE[r.missedReason ?? 'broken'];
}
export type BreakRequestInput = z.input<typeof BreakRequestSchema>;
export type BreakRequest = z.output<typeof BreakRequestSchema>;

/* ------------------------------------------------------------------------ */
/* Procedures on an appointment (NEW-WF-01: WF-02, SB-03/07/08, TP-14)       */
/* ------------------------------------------------------------------------ */

/** A planned procedure to create from a code ("Add code"); a Velora draft at a PMS office. */
export const NewPlannedProcedureSchema = z
  .object({
    code: z.string().trim().min(1).max(15),
    tooth: z.string().trim().min(1).max(4).optional(),
    surfaces: z.string().trim().min(1).max(8).optional(),
  })
  .strict();
export type NewPlannedProcedure = z.infer<typeof NewPlannedProcedureSchema>;

/** `POST /v1/schedule/appointments/:id/procedures`: attach planned procedures. */
export const AttachProceduresRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    expectedVersion: RowVersionSchema,
    /** This patient's treatment-planned rows (status 1 TP or 8 TPi; never conditions). */
    procedureIds: z.array(IdSchema).max(50).default([]),
    /** Codes to plan and attach in one step. */
    codes: z.array(NewPlannedProcedureSchema).max(20).default([]),
    onConflict: ProcedureConflictChoiceSchema.default('ask'),
  })
  .strict()
  .refine((r) => r.procedureIds.length + r.codes.length > 0, {
    message: 'send at least one procedure or code',
  });
export type AttachProceduresRequestInput = z.input<typeof AttachProceduresRequestSchema>;
export type AttachProceduresRequest = z.output<typeof AttachProceduresRequestSchema>;

/** `DELETE /v1/schedule/appointments/:id/procedures`: detach (the procedures stay planned). */
export const DetachProceduresRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    expectedVersion: RowVersionSchema,
    procedureIds: z.array(IdSchema).min(1).max(50),
  })
  .strict();
export type DetachProceduresRequest = z.infer<typeof DetachProceduresRequestSchema>;

/** One procedure that already sits on another live upcoming appointment. */
export const ProcedureAttachConflictSchema = z.object({
  procedureId: IdSchema,
  code: z.string(),
  tooth: z.string().nullable(),
  surfaces: z.string().nullable(),
  appointmentId: IdSchema,
  startsAt: InstantSchema.nullable(),
  providerAbbr: z.string().nullable(),
});
export type ProcedureAttachConflict = z.infer<typeof ProcedureAttachConflictSchema>;

/** `details` of the 409 `conflict` error a procedure on another appointment raises (SB-08). */
export const ProcedureConflictDetailsSchema = z.object({
  kind: z.literal('procedure_conflict'),
  conflicts: z.array(ProcedureAttachConflictSchema),
});
export type ProcedureConflictDetails = z.infer<typeof ProcedureConflictDetailsSchema>;

export const AppointmentProceduresResultSchema = ScheduleWriteResultSchema.extend({
  attached: z.array(IdSchema),
  detached: z.array(IdSchema),
  /** Taken off another appointment (the Move choice). */
  moved: z.array(z.object({ procedureId: IdSchema, fromAppointmentId: IdSchema })),
  /** Left on their other appointment (the Keep choice). */
  kept: z.array(IdSchema),
  /** Planned procedures created from `codes`. */
  created: z.array(IdSchema),
});
export type AppointmentProceduresResult = z.infer<typeof AppointmentProceduresResultSchema>;

/** `GET /v1/schedule/patients/:id/planned-procedures?locationId=` (the panel's "Add from treatment plan"). */
export const PlannedProceduresQuerySchema = z.object({ locationId: IdSchema });

export const PlannedProcedureSchema = z.object({
  procedureId: IdSchema,
  code: z.string(),
  abbreviation: z.string().nullable(),
  description: z.string().nullable(),
  tooth: z.string().nullable(),
  surfaces: z.string().nullable(),
  /** 1 TP or 8 TPi. */
  status: z.union([z.literal(1), z.literal(8)]),
  providerId: IdSchema.nullable(),
  /** Null without `money.view`. */
  feeCents: z.number().int().nullable(),
  /** Insurance estimate from the estimate engine; null without a plan or `money.view`. */
  insEstimateCents: z.number().int().nullable(),
  /** Fee minus the insurance estimate; null when either is unknown. */
  patientPortionCents: z.number().int().nullable(),
  /** The appointment it already sits on, if any (live or not). */
  appointmentId: IdSchema.nullable(),
  appointmentStartsAt: InstantSchema.nullable(),
  /** That appointment is live and not in the past: attaching it here asks Keep / Move / Back. */
  onUpcomingAppointment: z.boolean(),
  /** `velora`: a Velora draft at a PMS office (not in the PMS). */
  origin: z.enum(['pms', 'velora']),
  treatmentPlanName: z.string().nullable(),
});
export type PlannedProcedure = z.infer<typeof PlannedProcedureSchema>;

export const PlannedProceduresResultSchema = z.object({
  procedures: z.array(PlannedProcedureSchema),
  /**
   * Null where Velora is the system of record. Otherwise the line the panel
   * shows: no PulseSync command attaches procedures to a PMS appointment yet,
   * so attachments are kept in Velora and never claimed as synced.
   */
  pmsNotice: z.string().nullable(),
});
export type PlannedProceduresResult = z.infer<typeof PlannedProceduresResultSchema>;

/* ------------------------------------------------------------------------ */
/* Detail and history                                                        */
/* ------------------------------------------------------------------------ */

/** Another member of the household booked the same day ("pin family"). */
export const RelatedAppointmentSchema = z.object({
  id: IdSchema,
  patient: AppointmentPatientRefSchema,
  startsAt: InstantSchema.nullable(),
  lengthMin: LengthMinSchema,
  operatoryId: IdSchema.nullable(),
  providerId: IdSchema.nullable(),
  aptStatus: AptStatusSchema,
});
export type RelatedAppointment = z.infer<typeof RelatedAppointmentSchema>;

export const LastCommandSchema = z.object({
  id: IdSchema,
  type: z.string().max(64),
  status: z.string().max(32),
  createdAt: InstantSchema,
  /** Failure text without PHI, for the write-back tooltip. */
  error: z.string().max(500).nullable(),
});
export type LastCommand = z.infer<typeof LastCommandSchema>;

/* ------------------------------------------------------------------------ */
/* Pinboard and lists (sched-move 4.1, 4.4)                                  */
/* ------------------------------------------------------------------------ */

/** HOLD keeps the slot; RELEASED frees it (OD: UnschedList). */
export const PINBOARD_MODES = ['hold', 'released'] as const;
export const PinboardModeSchema = z.enum(PINBOARD_MODES);
export type PinboardMode = z.infer<typeof PinboardModeSchema>;

/** A recall-coded pinboard item with no appointment (WF-09). */
export const PinboardDraftSchema = z.object({
  kind: z.literal('recall'),
  patientId: IdSchema,
  /** Null in privacy mode or without the patient's name. */
  patientLabel: z.string().max(200).nullable(),
  typeKey: z.string().max(40).nullable(),
  typeName: z.string().max(120).nullable(),
  codes: z.array(z.string().max(12)).max(12),
  lengthMin: LengthMinSchema.nullable(),
  timePattern: TimePatternSchema.nullable(),
  dueDate: LocalDateSchema.nullable(),
});
export type PinboardDraft = z.infer<typeof PinboardDraftSchema>;

export const PinboardItemSchema = z.object({
  id: IdSchema,
  ...LocationOwnedSchema.shape,
  userId: IdSchema,
  userName: z.string().max(200).nullable(),
  /** True when the item is on the caller's own pinboard (Mine tab). */
  mine: z.boolean(),
  appointmentId: IdSchema.nullable(),
  mode: PinboardModeSchema,
  note: z.string().max(1000).nullable(),
  /** Where the appointment was when it was pinned. */
  was: z
    .object({
      startsAt: InstantSchema,
      endsAt: InstantSchema,
      operatoryId: IdSchema.nullable(),
      providerId: IdSchema.nullable(),
    })
    .nullable(),
  /** `deferred`: a cut that is not written to the PMS until placed. */
  pmsWrite: z.enum(['none', 'queued', 'deferred']),
  /** HOLD older than the office's stale-hold limit. */
  stale: z.boolean(),
  appointment: AppointmentCardSchema.nullable(),
  /**
   * A booking still to make (OD Make Recall / Make Fam Recall, WF-09): no
   * appointment yet, only the recall's codes, length and due date. Kept in
   * Velora; placing it opens the Book panel seeded with them.
   */
  draft: PinboardDraftSchema.nullable().optional(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
  version: RowVersionSchema,
});
export type PinboardItem = z.infer<typeof PinboardItemSchema>;

/** `POST /v1/schedule/pinboard/drafts`: Make Recall / Make Fam Recall (one item per member). */
export const RecallDraftsRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    locationId: IdSchema,
    items: z
      .array(
        z
          .object({
            patientId: IdSchema,
            typeKey: z.string().trim().max(40).nullable().default(null),
            typeName: z.string().trim().max(120).nullable().default(null),
            codes: z.array(z.string().trim().min(1).max(12)).max(12).default([]),
            lengthMin: LengthMinSchema.nullable().default(null),
            timePattern: TimePatternSchema.nullable().default(null),
            dueDate: LocalDateSchema.nullable().default(null),
          })
          .strict(),
      )
      .min(1)
      .max(12),
  })
  .strict();
export type RecallDraftsRequest = z.input<typeof RecallDraftsRequestSchema>;
export type RecallDraftsBody = z.output<typeof RecallDraftsRequestSchema>;

export const RecallDraftsResultSchema = z.object({
  items: z.array(PinboardItemSchema),
  /** Members already on the pinboard for the same recall (not added again). */
  skipped: z.number().int(),
});
export type RecallDraftsResult = z.infer<typeof RecallDraftsResultSchema>;

/**
 * Hover card and panel header facts about the patient (BUILD-PLAN 3.5 hover
 * card). Null in privacy mode or without `patient.view`.
 */
export const AppointmentPatientSummarySchema = z.object({
  birthdate: LocalDateSchema.nullable(),
  /** Age on the appointment's day. */
  age: z.number().int().nonnegative().nullable(),
  chartNumber: z.string().max(40).nullable(),
  /** Primary phone, mobile preferred. */
  phone: z.string().max(40).nullable(),
  /** "Primary: Delta Dental (group 1234)". */
  insurance: z.array(z.string().max(200)),
  /** Office-local date of the last completed visit at this office. */
  lastVisit: LocalDateSchema.nullable(),
  /** The next booked visit after this one, at this office. */
  nextVisit: InstantSchema.nullable(),
  /** The shared recall answer (hygiene recall first), the same date the Recall card shows. */
  recallDue: LocalDateSchema.nullable(),
  /** Where `recallDue` comes from: Velora (work done here) or the PMS. Absent from older servers. */
  recallSource: z.enum(['velora', 'pms', 'manual', 'none']).optional(),
  recallType: z.string().max(80).nullable().optional(),
});
export type AppointmentPatientSummary = z.infer<typeof AppointmentPatientSummarySchema>;

/**
 * Why a broken Dentrix appointment was broken. Dentrix has no reason column:
 * staff write it in a contact-log entry (contact type 7) on the patient,
 * dated the broken day. Read from the PMS communication log Velora already
 * mirrors (pms_commlog); nothing new is stored.
 */
export const BrokenReasonSchema = z.object({
  /** The contact-log text. Free text is PHI: null when names are hidden. */
  note: z.string().max(20000).nullable(),
  /** When the contact-log entry was made. */
  at: InstantSchema,
  /**
   * Which day matched: the broken slot's day (Dentrix brokendate), the day it
   * was broken, or (neither sent) the appointment's own date.
   */
  matchedOn: z.enum(['broken_date', 'broken_on', 'appointment_date']),
});
export type BrokenReason = z.infer<typeof BrokenReasonSchema>;

/** Detail of one appointment for the docked panel. */
export const AppointmentDetailSchema = z.object({
  appointment: AppointmentCardSchema,
  patientSummary: AppointmentPatientSummarySchema.nullable(),
  /** Newest first ("Moved by Dana"). */
  events: z.array(AppointmentEventSchema),
  related: z.array(RelatedAppointmentSchema),
  pinboardItem: PinboardItemSchema.nullable(),
  lastCommand: LastCommandSchema.nullable(),
  /** Broken Dentrix appointments: the contact-log reason; null when none is found. */
  brokenReason: BrokenReasonSchema.nullable().optional(),
});
export type AppointmentDetail = z.infer<typeof AppointmentDetailSchema>;

export const PinboardQuerySchema = z.object({
  locationId: IdSchema,
  scope: z.enum(['mine', 'team']).default('mine'),
  /** Patient name, procedure code, provider abbreviation or original date (YYYY-MM-DD). */
  q: z.string().trim().max(80).optional(),
});
export const PinboardListSchema = z.object({ items: z.array(PinboardItemSchema) });
export type PinboardList = z.infer<typeof PinboardListSchema>;

/** `POST /v1/schedule/appointments/:id/pin`. */
export const PinRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    expectedVersion: RowVersionSchema,
    mode: PinboardModeSchema.default('hold'),
    note: z.string().trim().max(1000).optional(),
  })
  .strict();
export type PinRequestInput = z.input<typeof PinRequestSchema>;

/** `PATCH /v1/schedule/pinboard/:id`: toggle HOLD/RELEASED or edit the note. */
export const PinboardPatchSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    expectedVersion: RowVersionSchema,
    mode: PinboardModeSchema.optional(),
    note: z.string().trim().max(1000).nullable().optional(),
  })
  .strict()
  .refine((r) => r.mode !== undefined || r.note !== undefined, 'change mode or note');
export type PinboardPatch = z.infer<typeof PinboardPatchSchema>;

/** `POST /v1/schedule/pinboard/:id/place`: put a pinned appointment in a slot. */
export const PlaceRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    /** The appointment's row version. */
    expectedVersion: RowVersionSchema,
    placement: PlacementSchema,
    leaseId: IdSchema.optional(),
    override: ConflictOverrideSchema.optional(),
  })
  .strict()
  .superRefine((r, ctx) => {
    checkPatternLength(r.placement, ctx);
  });
export type PlaceRequest = z.infer<typeof PlaceRequestSchema>;

export const PinboardResultSchema = z.object({
  item: PinboardItemSchema.nullable(),
  write: ScheduleWriteResultSchema.nullable(),
});
export type PinboardResult = z.infer<typeof PinboardResultSchema>;

/** Rebooking lists beside the pinboard. */
export const SCHEDULE_LISTS = ['asap', 'broken', 'unscheduled'] as const;
export const ScheduleListNameSchema = z.enum(SCHEDULE_LISTS);
export type ScheduleListName = z.infer<typeof ScheduleListNameSchema>;
/** Untouched list entries move to Archived after this many days (sched-move 4.4). */
export const LIST_ARCHIVE_DAYS = 90;

export const ScheduleListQuerySchema = z.object({
  locationId: IdSchema,
  list: ScheduleListNameSchema,
  q: z.string().trim().max(80).optional(),
  /** `true`: the Archived view (untouched 90 days, removed with a reason, rebooked). */
  includeArchived: BoolParam.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
  /** Paging beyond the first page (McLaren's "Broken 200", SM-20). */
  offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
});

/** Why an entry was taken off a list (remove with reason, SM-20). */
export const LIST_REMOVE_REASONS = [
  'rebooked_elsewhere',
  'patient_declined',
  'unreachable',
  'treatment_done_elsewhere',
  'moved_away',
  'duplicate',
  'other',
] as const;
export const ListRemoveReasonSchema = z.enum(LIST_REMOVE_REASONS);
export type ListRemoveReason = z.infer<typeof ListRemoveReasonSchema>;

export const ListRemovalSchema = z.object({
  reason: ListRemoveReasonSchema,
  note: z.string().max(500).nullable(),
  removedAt: InstantSchema,
  removedByName: z.string().max(200).nullable(),
});
export type ListRemoval = z.infer<typeof ListRemovalSchema>;

/** One attempt to reach a patient from a list (contact log, EX-08). */
export const LIST_CONTACT_KINDS = ['call', 'text', 'email', 'note'] as const;
export const LIST_CONTACT_OUTCOMES = [
  'reached',
  'left_message',
  'no_answer',
  'wrong_number',
  'booked',
  'declined',
] as const;
export const ListContactSchema = z.object({
  id: IdSchema,
  patientId: IdSchema,
  list: z.enum([...SCHEDULE_LISTS, 'unconfirmed', 'recall']),
  appointmentId: IdSchema.nullable(),
  asapEntryId: IdSchema.nullable(),
  kind: z.enum(LIST_CONTACT_KINDS),
  outcome: z.enum(LIST_CONTACT_OUTCOMES).nullable(),
  /** Free text is PHI: null when names are hidden. */
  note: z.string().max(1000).nullable(),
  byName: z.string().max(200).nullable(),
  at: InstantSchema,
});
export type ListContact = z.infer<typeof ListContactSchema>;

export const ScheduleListItemSchema = z.object({
  appointment: AppointmentCardSchema,
  archived: z.boolean(),
  lastTouchedAt: InstantSchema,
  /** Set in the Archived view when someone took it off the list. */
  removed: ListRemovalSchema.nullable().optional(),
  /** The newest contact-log entry for this patient on this list. */
  lastContact: ListContactSchema.nullable().optional(),
  /** Broken list, Dentrix: the PMS contact-log reason; null when none is found. */
  brokenReason: BrokenReasonSchema.nullable().optional(),
});

/* ASAP entries: a Velora-owned list, independent of PMS priority (W-11). */

export const PartOfDaySchema = z.enum(['any', 'am', 'pm']);
export type PartOfDay = z.infer<typeof PartOfDaySchema>;

/** An ASAP list entry, with or without an existing appointment (MM-05, AM-21). */
export const AsapEntrySchema = z.object({
  id: IdSchema,
  /** The entry's office; null when it is open to several offices (`offices`). */
  locationId: IdSchema.nullable(),
  /** Offices where the patient would come (at least one). */
  offices: z.array(IdSchema).min(1),
  patientId: IdSchema,
  patient: AppointmentPatientRefSchema.nullable(),
  /** The booked visit the patient wants sooner; null for "no appointment yet". */
  appointmentId: IdSchema.nullable(),
  providerId: IdSchema.nullable(),
  appointmentTypeId: IdSchema.nullable(),
  appointmentTypeName: z.string().max(100).nullable(),
  lengthMin: LengthMinSchema,
  /** Days the patient can come, 0 = Sunday; empty = any day. */
  weekdays: z.array(z.number().int().min(0).max(6)).max(7),
  partOfDay: PartOfDaySchema,
  /** The patient needs at least this much notice. */
  noticeHours: NoticeHoursSchema,
  /** Office-local last day the entry is good for; null = no expiry. */
  expiresOn: LocalDateSchema.nullable(),
  /** PHI: null when names are hidden. */
  note: z.string().max(1000).nullable(),
  expired: z.boolean(),
  removed: ListRemovalSchema.nullable(),
  lastContact: ListContactSchema.nullable(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
  version: RowVersionSchema,
});
export type AsapEntry = z.infer<typeof AsapEntrySchema>;

export const ScheduleListSchema = z.object({
  list: ScheduleListNameSchema,
  items: z.array(ScheduleListItemSchema),
  truncated: z.boolean(),
  /** The `offset` of the next page, or null on the last page. */
  nextOffset: z.number().int().nonnegative().nullable().optional(),
  /** ASAP list only: Velora's own entries (with or without an appointment). */
  entries: z.array(AsapEntrySchema).optional(),
});
export type ScheduleList = z.infer<typeof ScheduleListSchema>;

const WeekdaysSchema = z
  .array(z.number().int().min(0).max(6))
  .max(7)
  .refine((d) => new Set(d).size === d.length, 'weekdays repeat');

/** `POST /v1/schedule/asap`. */
export const AsapEntryCreateSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    locationId: IdSchema,
    patientId: IdSchema,
    appointmentId: IdSchema.nullable().default(null),
    providerId: IdSchema.nullable().default(null),
    appointmentTypeId: IdSchema.nullable().default(null),
    lengthMin: LengthMinSchema.default(60),
    weekdays: WeekdaysSchema.default([]),
    partOfDay: PartOfDaySchema.default('any'),
    noticeHours: NoticeHoursSchema.default(0),
    expiresOn: LocalDateSchema.nullable().default(null),
    note: z.string().trim().max(1000).nullable().default(null),
    /** Other offices the patient would also come to (multi-location ASAP, AM-21). */
    alsoOffices: z.array(IdSchema).max(20).default([]),
  })
  .strict();
export type AsapEntryCreateInput = z.input<typeof AsapEntryCreateSchema>;
export type AsapEntryCreate = z.output<typeof AsapEntryCreateSchema>;

/** `PATCH /v1/schedule/asap/:id`. */
export const AsapEntryPatchSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    expectedVersion: RowVersionSchema,
    providerId: IdSchema.nullable().optional(),
    lengthMin: LengthMinSchema.optional(),
    weekdays: WeekdaysSchema.optional(),
    partOfDay: PartOfDaySchema.optional(),
    noticeHours: NoticeHoursSchema.optional(),
    expiresOn: LocalDateSchema.nullable().optional(),
    note: z.string().trim().max(1000).nullable().optional(),
  })
  .strict();
export type AsapEntryPatch = z.infer<typeof AsapEntryPatchSchema>;

export const AsapEntryResultSchema = z.object({ entry: AsapEntrySchema });
export type AsapEntryResult = z.infer<typeof AsapEntryResultSchema>;

/**
 * `POST /v1/schedule/lists/remove`: take an appointment (Broken, Unscheduled,
 * ASAP) or an ASAP entry off its list with a reason. Nothing is deleted and
 * nothing is written to the PMS; it shows under Archived and can be restored.
 */
export const ListRemoveRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    list: ScheduleListNameSchema,
    appointmentId: IdSchema.optional(),
    asapEntryId: IdSchema.optional(),
    reason: ListRemoveReasonSchema,
    note: z.string().trim().max(500).optional(),
  })
  .strict()
  .refine(
    (r) => (r.appointmentId === undefined) !== (r.asapEntryId === undefined),
    'send exactly one of appointmentId or asapEntryId',
  );
export type ListRemoveRequest = z.infer<typeof ListRemoveRequestSchema>;

/** `POST /v1/schedule/lists/restore`: put a removed entry back on its list. */
export const ListRestoreRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    list: ScheduleListNameSchema,
    appointmentId: IdSchema.optional(),
    asapEntryId: IdSchema.optional(),
  })
  .strict()
  .refine(
    (r) => (r.appointmentId === undefined) !== (r.asapEntryId === undefined),
    'send exactly one of appointmentId or asapEntryId',
  );
export type ListRestoreRequest = z.infer<typeof ListRestoreRequestSchema>;

export const ListChangeResultSchema = z.object({ ok: z.literal(true) });

/** `POST /v1/schedule/lists/contacts`: log a call, text or note from a list card. */
export const ListContactCreateSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    patientId: IdSchema,
    list: ListContactSchema.shape.list,
    appointmentId: IdSchema.optional(),
    asapEntryId: IdSchema.optional(),
    kind: z.enum(LIST_CONTACT_KINDS),
    outcome: z.enum(LIST_CONTACT_OUTCOMES).optional(),
    note: z.string().trim().max(1000).optional(),
  })
  .strict();
export type ListContactCreate = z.infer<typeof ListContactCreateSchema>;
export const ListContactResultSchema = z.object({ contact: ListContactSchema });

export const ListContactQuerySchema = z.object({
  locationId: IdSchema,
  patientId: IdSchema,
});
export const ListContactListSchema = z.object({ contacts: z.array(ListContactSchema) });
export type ListContactList = z.infer<typeof ListContactListSchema>;

/* Fill candidates without an appointment (EX-10, MM-05; feeds IMP-13). */

export const FillCandidatesQuerySchema = z.object({
  locationId: IdSchema,
  /** The opening's office-local date. */
  date: LocalDateSchema,
  limit: z.coerce.number().int().min(1).max(200).default(100),
});

export const FillCandidateSchema = z.object({
  /**
   * `treatment`: unscheduled treatment (the Treatment Plans definition,
   * shared `unscheduled_treatment` metric): doctor time with no visit.
   */
  source: z.enum(['asap_entry', 'recall', 'treatment']),
  key: z.string().max(80),
  patientId: IdSchema,
  patient: AppointmentPatientRefSchema.nullable(),
  lengthMin: LengthMinSchema,
  providerId: IdSchema.nullable(),
  appointmentTypeId: IdSchema.nullable(),
  appointmentTypeName: z.string().max(100).nullable(),
  /** The ASAP entry behind an `asap_entry` candidate. */
  asapEntry: AsapEntrySchema.nullable(),
  /** The recall behind a `recall` candidate. */
  recall: z
    .object({
      recallId: IdSchema,
      dueDate: LocalDateSchema,
      typeName: z.string().max(100).nullable(),
    })
    .nullable(),
  /** The unscheduled treatment behind a `treatment` candidate (null cents without money.view). */
  treatment: z
    .object({
      cents: z.number().int().nonnegative().nullable(),
      codes: z.array(z.string().max(15)).max(20),
    })
    .nullable()
    .optional(),
});
export type FillCandidate = z.infer<typeof FillCandidateSchema>;
export const FillCandidatesResultSchema = z.object({
  date: LocalDateSchema,
  candidates: z.array(FillCandidateSchema),
  truncated: z.boolean(),
});
export type FillCandidatesResult = z.infer<typeof FillCandidatesResultSchema>;

/* Household and series booking (SB-15, RC-13, WF-05). */

/** One appointment of a compound create (a family member or one visit of a series). */
export const CompoundCreateItemSchema = z
  .object({
    patientId: IdSchema,
    operatoryId: IdSchema,
    providerId: IdSchema,
    secondaryProviderId: IdSchema.nullable().default(null),
    appointmentTypeId: IdSchema.nullable().default(null),
    startsAt: InstantSchema,
    lengthMin: LengthMinSchema,
    timePattern: TimePatternSchema.nullable().default(null),
    isNewPatient: z.boolean().default(false),
    note: z.string().max(4000).nullable().default(null),
    procedureIds: z.array(IdSchema).max(50).default([]),
    leaseId: IdSchema.optional(),
  })
  .strict()
  .superRefine(checkPatternLength);
export type CompoundCreateItemInput = z.input<typeof CompoundCreateItemSchema>;
export type CompoundCreateItem = z.output<typeof CompoundCreateItemSchema>;

export const HOUSEHOLD_MAX_MEMBERS = 8;

/**
 * `POST /v1/schedule/appointments/household`: book several family members in
 * one compound command, all or none. One undo entry; each appointment gets
 * its own PMS command (like a multi-select move).
 */
export const HouseholdBookRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    locationId: IdSchema,
    items: z.array(CompoundCreateItemSchema).min(2).max(HOUSEHOLD_MAX_MEMBERS),
    override: ConflictOverrideSchema.optional(),
  })
  .strict()
  .refine(
    (r) => new Set(r.items.map((i) => i.patientId.toLowerCase())).size === r.items.length,
    'each family member is booked once',
  );
export type HouseholdBookRequestInput = z.input<typeof HouseholdBookRequestSchema>;
export type HouseholdBookRequest = z.output<typeof HouseholdBookRequestSchema>;

export const CompoundCreateResultSchema = z.object({
  results: z.array(ScheduleWriteResultSchema),
  actionId: IdSchema.nullable(),
});
export type CompoundCreateResult = z.infer<typeof CompoundCreateResultSchema>;

/** The `details` of a refused household booking: which member failed and why. */
export const CompoundCreateFailureSchema = z.object({
  index: z.number().int().nonnegative(),
  patientId: IdSchema,
  issues: z.array(ScheduleIssueSchema),
});

export const SERIES_MAX_COUNT = 52;

/**
 * `POST /v1/schedule/appointments/series` (and `/series/preview`): every N
 * weeks x count from the first visit, at the same office-local time. With
 * `onConflict: 'skip'` the clean visits are booked and each conflict is
 * reported; with `'stop'` any conflict refuses the whole series.
 */
export const SeriesRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    locationId: IdSchema,
    first: CompoundCreateItemSchema,
    everyWeeks: z.number().int().min(1).max(26),
    count: z.number().int().min(2).max(SERIES_MAX_COUNT),
    onConflict: z.enum(['skip', 'stop']).default('skip'),
    override: ConflictOverrideSchema.optional(),
  })
  .strict();
export type SeriesRequestInput = z.input<typeof SeriesRequestSchema>;
export type SeriesRequest = z.output<typeof SeriesRequestSchema>;

export const SeriesOccurrenceSchema = z.object({
  index: z.number().int().nonnegative(),
  startsAt: InstantSchema,
  status: z.enum(['booked', 'conflict', 'clean']),
  appointmentId: IdSchema.nullable(),
  issues: z.array(ScheduleIssueSchema),
  message: z.string().max(500).nullable(),
});
export type SeriesOccurrence = z.infer<typeof SeriesOccurrenceSchema>;

export const SeriesResultSchema = z.object({
  /** `preview`: nothing was written; `clean` marks a visit that would book. */
  preview: z.boolean(),
  occurrences: z.array(SeriesOccurrenceSchema),
  results: z.array(ScheduleWriteResultSchema),
  actionId: IdSchema.nullable(),
});
export type SeriesResult = z.infer<typeof SeriesResultSchema>;

/** `GET /v1/schedule/household`: the family to book together. */
export const HouseholdQuerySchema = z.object({ locationId: IdSchema, patientId: IdSchema });
export const HouseholdMemberSchema = z.object({
  patientId: IdSchema,
  patient: AppointmentPatientRefSchema,
  relationship: z.string().max(20).nullable(),
  /** Earliest active recall due date. */
  recallDue: LocalDateSchema.nullable(),
  /** Length suggested by the recall type's appointment type. */
  suggestedLengthMin: LengthMinSchema.nullable(),
  suggestedAppointmentTypeId: IdSchema.nullable(),
  /** The next booked visit at any office, so the desk does not double book. */
  nextVisit: InstantSchema.nullable(),
  active: z.boolean(),
});
export type HouseholdMember = z.infer<typeof HouseholdMemberSchema>;
export const HouseholdSchema = z.object({
  householdId: IdSchema.nullable(),
  /** `household`: synced or Velora household; `patient_only`: none on file, pick members by hand. */
  source: z.enum(['household', 'patient_only']),
  members: z.array(HouseholdMemberSchema),
});
export type Household = z.infer<typeof HouseholdSchema>;

/** `POST /v1/schedule/household/slots`: adjacent or parallel slots for the family. */
export const HouseholdSlotsRequestSchema = z
  .object({
    locationId: IdSchema,
    members: z
      .array(
        z
          .object({
            patientId: IdSchema,
            lengthMin: LengthMinSchema,
            providerId: IdSchema.optional(),
            timePattern: TimePatternSchema.optional(),
          })
          .strict(),
      )
      .min(2)
      .max(HOUSEHOLD_MAX_MEMBERS),
    /** First day to search; default today, moved to the latest recall due date when later. */
    from: LocalDateSchema.optional(),
    /** Ignore the recall due dates (search from `from`). */
    ignoreRecallDue: z.boolean().default(false),
    days: z.number().int().min(1).max(60).default(21),
    arrangement: z.enum(['any', 'parallel', 'adjacent']).default('any'),
    limit: z.number().int().min(1).max(20).default(6),
  })
  .strict();
export type HouseholdSlotsRequestInput = z.input<typeof HouseholdSlotsRequestSchema>;
export type HouseholdSlotsRequest = z.output<typeof HouseholdSlotsRequestSchema>;

export const HouseholdSlotSchema = z.object({
  patientId: IdSchema,
  operatoryId: IdSchema,
  providerId: IdSchema,
  startsAt: InstantSchema,
  endsAt: InstantSchema,
});
export const HouseholdOptionSchema = z.object({
  arrangement: z.enum(['parallel', 'adjacent']),
  date: LocalDateSchema,
  slots: z.array(HouseholdSlotSchema),
});
export type HouseholdOption = z.infer<typeof HouseholdOptionSchema>;
export const HouseholdSlotsResultSchema = z.object({
  searchedFrom: LocalDateSchema,
  searchedTo: LocalDateSchema,
  /** The latest recall due date among the members, when it moved the search. */
  latestRecallDue: LocalDateSchema.nullable(),
  options: z.array(HouseholdOptionSchema),
  reason: z.enum(['office_hours_not_set']).nullable(),
});
export type HouseholdSlotsResult = z.infer<typeof HouseholdSlotsResultSchema>;

/* ------------------------------------------------------------------------ */
/* Day notes and blockouts                                                   */
/* ------------------------------------------------------------------------ */

/** `PUT /v1/schedule/day-notes`: upsert; an empty body removes the note. */
export const DayNotePutSchema = z
  .object({
    locationId: IdSchema,
    day: LocalDateSchema,
    operatoryId: IdSchema.nullable().default(null),
    body: z.string().max(4000),
  })
  .strict();
export type DayNotePutInput = z.input<typeof DayNotePutSchema>;
export const DayNoteResultSchema = z.object({ note: DayNoteSchema.nullable() });

/** RFC 5545 subset Velora understands: weekly (every N weeks) on given days, optional end. */
export const RECURRENCE_RULE_RE =
  /^FREQ=WEEKLY(?:;INTERVAL=(?:[1-9]|[1-4]\d|5[0-2]))?;BYDAY=(?:MO|TU|WE|TH|FR|SA|SU)(?:,(?:MO|TU|WE|TH|FR|SA|SU))*(?:;UNTIL=\d{8})?$/;
export const RecurrenceRuleSchema = z
  .string()
  .regex(RECURRENCE_RULE_RE, 'recurrence is FREQ=WEEKLY[;INTERVAL=n];BYDAY=..[;UNTIL=YYYYMMDD]');

export const BlockoutCreateSchema = z
  .object({
    locationId: IdSchema,
    blockoutType: z.string().trim().min(1).max(100),
    operatoryIds: z.array(IdSchema).min(1).max(200),
    providerId: IdSchema.nullable().default(null),
    startsAt: InstantSchema,
    endsAt: InstantSchema,
    note: z.string().trim().max(1000).nullable().default(null),
    recurrenceRule: RecurrenceRuleSchema.nullable().default(null),
  })
  .strict()
  .superRefine((b, ctx) => {
    const span = Date.parse(b.endsAt) - Date.parse(b.startsAt);
    if (span <= 0) {
      ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'endsAt must be after startsAt' });
    } else if (span > DAY_MS) {
      ctx.addIssue({
        code: 'custom',
        path: ['endsAt'],
        message: 'a blockout covers at most one day; use a recurrence for more',
      });
    }
  });
export type BlockoutCreateInput = z.input<typeof BlockoutCreateSchema>;

export const BlockoutPatchSchema = z
  .object({
    expectedVersion: RowVersionSchema,
    blockoutType: z.string().trim().min(1).max(100).optional(),
    operatoryIds: z.array(IdSchema).min(1).max(200).optional(),
    providerId: IdSchema.nullable().optional(),
    startsAt: InstantSchema.optional(),
    endsAt: InstantSchema.optional(),
    note: z.string().trim().max(1000).nullable().optional(),
    recurrenceRule: RecurrenceRuleSchema.nullable().optional(),
  })
  .strict()
  .refine(
    (b) => Object.entries(b).some(([k, v]) => k !== 'expectedVersion' && v !== undefined),
    'change at least one field',
  );
export type BlockoutPatch = z.infer<typeof BlockoutPatchSchema>;

export const BlockoutDeleteQuerySchema = z.object({
  expectedVersion: z.coerce.number().int().positive(),
});

export const BlockoutResultSchema = z.object({ blockout: BlockoutSchema.nullable() });

/* ------------------------------------------------------------------------ */
/* Find openings (sched-book 4.3)                                            */
/* ------------------------------------------------------------------------ */

export const OpeningsQuerySchema = z.object({
  locationId: IdSchema,
  lengthMin: z.coerce
    .number()
    .int()
    .positive()
    .max(1440)
    .refine((n) => n % PATTERN_MINUTES_PER_CHAR === 0, 'length must be a multiple of 5 minutes'),
  timePattern: TimePatternSchema.optional(),
  providerId: IdSchema.optional(),
  operatoryIds: z.string().regex(CSV_IDS_RE).optional(),
  /** First office-local day to search; default today. */
  from: LocalDateSchema.optional(),
  days: z.coerce.number().int().min(1).max(90).default(14),
  partOfDay: z.enum(['any', 'am', 'pm']).default('any'),
  /** Preferred weekdays, 0 = Sunday, comma separated. */
  weekdays: z
    .string()
    .regex(/^[0-6](?:,[0-6])*$/)
    .optional(),
  /** Moving an existing appointment: its own slot does not count as busy. */
  appointmentId: IdSchema.optional(),
  /**
   * The patient being booked: when the office's patient grade prime-time rule
   * applies to them, prime-time openings carry `policy` and come last.
   */
  patientId: IdSchema.optional(),
  /** The visit type (an emergency type is never restricted by a grade). */
  appointmentTypeId: IdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(20).default(5),
});
export type OpeningsQuery = z.infer<typeof OpeningsQuerySchema>;

export const OpeningSchema = z.object({
  date: LocalDateSchema,
  startMinute: LocalMinute,
  startsAt: InstantSchema,
  endsAt: InstantSchema,
  operatoryId: IdSchema,
  providerId: IdSchema.nullable(),
  /** Higher is better; for ordering only. */
  score: z.number(),
  matchesTemplate: z.boolean(),
  /**
   * Other chairs the same provider could take at the same time (one chip
   * per provider and time; the booking may pick any of these chairs).
   */
  alsoOperatoryIds: z.array(IdSchema).max(40).optional(),
  /**
   * Why this provider works this chair then: the PMS / Velora provider
   * schedule, the chair's default provider, the office's recent book
   * ("inferred from recent schedule", with its confidence), or only because
   * the staff asked for this provider. Absent from older servers.
   */
  providerSource: z.enum(['schedule', 'chair_default', 'inferred', 'requested']).optional(),
  providerConfidence: z.enum(['high', 'medium', 'low']).nullable().optional(),
  /** Soft warnings the booking would carry; blocking slots are never offered. */
  warnings: z.array(ScheduleIssueSchema),
  /** Patient grade office policy that applies to this slot for the patient (staff may still book it). */
  policy: z
    .object({ rule: z.literal('primeTime'), staffText: z.string().max(400) })
    .nullable()
    .optional(),
});
export type Opening = z.infer<typeof OpeningSchema>;

/* ------------------------------------------------------------------------ */
/* Slot check (one booking path, WF-12 / SB-05 / SB-06)                      */
/* ------------------------------------------------------------------------ */

/**
 * `POST /v1/schedule/appointments/check`: the verdict the save would get,
 * from the same rules engine as `createAppointment`, the approval queue and
 * checkout, so the panel's footer and the save never disagree.
 */
export const SlotCheckRequestSchema = z
  .object({
    locationId: IdSchema,
    /** Editing this appointment: its own slot is not an obstacle. */
    appointmentId: IdSchema.optional(),
    operatoryId: IdSchema,
    providerId: IdSchema.nullable(),
    secondaryProviderId: IdSchema.nullable().default(null),
    startsAt: InstantSchema,
    lengthMin: LengthMinSchema,
    timePattern: TimePatternSchema.nullable().default(null),
    /** The caller's own hold on the slot (never an obstacle to its holder). */
    leaseId: IdSchema.optional(),
    arriveEarlyMin: ArriveEarlySchema.nullable().default(null),
  })
  .strict()
  .superRefine(checkPatternLength);
export type SlotCheckRequest = z.infer<typeof SlotCheckRequestSchema>;
export type SlotCheckRequestInput = z.input<typeof SlotCheckRequestSchema>;

/**
 * - `ok`: saves as is;
 * - `warning`: saves, the toast carries the warnings;
 * - `needs_reason`: saves with `override.reason` (a third concurrent X, or a
 *   block the user may override with permission);
 * - `blocked`: cannot be saved here (Save is disabled up front).
 */
export const SlotVerdictSchema = z.enum(['ok', 'warning', 'needs_reason', 'blocked']);
export type SlotVerdict = z.infer<typeof SlotVerdictSchema>;

export const SlotCheckResultSchema = z.object({
  verdict: SlotVerdictSchema,
  issues: z.array(ScheduleIssueSchema),
  /** One-line summary for the footer ("X time overlaps 10:10-10:20"). */
  message: z.string().max(300).nullable(),
  /** The nearest later start in the same chair that clears every block ("Shift to 10:20"). */
  shift: z.object({ startsAt: InstantSchema, startMinute: LocalMinute }).nullable(),
  /** Next openings of this length when the slot cannot be used. */
  alternatives: z.array(OpeningSchema),
  /** Arrive-early problem (before opening), or null. */
  arriveEarlyProblem: z.string().max(300).nullable(),
});
export type SlotCheckResult = z.infer<typeof SlotCheckResultSchema>;

export const OpeningsResultSchema = z.object({
  locationId: IdSchema,
  timezone: IanaTimeZoneSchema,
  searchedFrom: LocalDateSchema,
  searchedTo: LocalDateSchema,
  openings: z.array(OpeningSchema),
  /** More openings exist beyond `limit`. */
  truncated: z.boolean(),
  /** Why the search found nothing by construction (S2): the office has no hours set. */
  reason: z.enum(['office_hours_not_set']).nullable().optional(),
  hoursSource: HoursSourceSchema.optional(),
});
export type OpeningsResult = z.infer<typeof OpeningsResultSchema>;

/* ------------------------------------------------------------------------ */
/* Undo (last 20 actions per user, server-backed; BUILD-PLAN 3.5)            */
/* ------------------------------------------------------------------------ */

export const SCHEDULE_UNDO_DEPTH = 20;
export const SCHEDULE_ACTION_KINDS = [
  'create',
  'move',
  'compound_move',
  'update',
  'break',
  'pin',
  'place',
  'undo',
  /** Procedures attached to or detached from an appointment (NEW-WF-01). */
  'procedures',
] as const;
export const ScheduleActionKindSchema = z.enum(SCHEDULE_ACTION_KINDS);
export type ScheduleActionKind = z.infer<typeof ScheduleActionKindSchema>;

export const ScheduleActionSchema = z.object({
  id: IdSchema,
  locationId: IdSchema,
  kind: ScheduleActionKindSchema,
  /** No patient names: "Moved 2 appointments". */
  label: z.string().max(200),
  appointmentIds: z.array(IdSchema),
  createdAt: InstantSchema,
  undoable: z.boolean(),
  undoneAt: InstantSchema.nullable(),
});
export type ScheduleAction = z.infer<typeof ScheduleActionSchema>;

export const ScheduleActionListQuerySchema = z.object({ locationId: IdSchema });
export const ScheduleActionListSchema = z.object({ actions: z.array(ScheduleActionSchema) });
export const UndoRequestSchema = z.object({ idempotencyKey: IdempotencyKeySchema }).strict();
export const UndoResultSchema = z.object({
  actionId: IdSchema,
  undoneActionId: IdSchema,
  appointments: z.array(AppointmentSchema),
  commandIds: z.array(IdSchema),
  warnings: z.array(ScheduleIssueSchema),
});
export type UndoResult = z.infer<typeof UndoResultSchema>;

/* ------------------------------------------------------------------------ */
/* Route slips (BUILD-PLAN 3.5 Print)                                        */
/* ------------------------------------------------------------------------ */

/** `GET /v1/schedule/route-slips`: a PDF, one slip per appointment. */
export const RouteSlipQuerySchema = z.object({
  locationId: IdSchema,
  date: LocalDateSchema,
  operatoryId: IdSchema.optional(),
  appointmentId: IdSchema.optional(),
  /** Only appointments not yet complete or broken. */
  remaining: BoolParam.optional(),
});
export type RouteSlipQuery = z.infer<typeof RouteSlipQuerySchema>;

/* ------------------------------------------------------------------------ */
/* Text an opening to the ASAP list (engagement gaps, task 4)                */
/* ------------------------------------------------------------------------ */

/**
 * Staff-triggered only: staff open an opening, preview who on the ASAP list
 * fits it and may be texted, pick, and send. Nothing is sent by a job. Each
 * text goes through the inbox (its thread, the office line and the consent
 * gates), so replies land in the inbox.
 */
export const AsapTextOpeningSchema = z.object({
  locationId: IdSchema,
  startsAt: InstantSchema,
  lengthMin: LengthMinSchema,
  providerId: IdSchema.nullable().optional(),
});
export type AsapTextOpening = z.infer<typeof AsapTextOpeningSchema>;

export const ASAP_TEXT_SKIP_REASONS = [
  'too_long',
  'wrong_day',
  'wrong_part_of_day',
  'short_notice',
  'expired',
  'other_provider',
  'already_earlier',
  'no_mobile',
  'opted_out',
  'consent_warning',
  'texted_recently',
] as const;
export type AsapTextSkipReason = (typeof ASAP_TEXT_SKIP_REASONS)[number];

export const AsapTextRecipientSchema = z.object({
  patientId: IdSchema,
  patient: AppointmentPatientRefSchema.nullable(),
  source: z.enum(['asap_entry', 'asap_appointment']),
  asapEntryId: IdSchema.nullable(),
  appointmentId: IdSchema.nullable(),
  /** Last four digits only, e.g. "***-***-0142". */
  phoneMasked: z.string().max(20).nullable(),
  eligible: z.boolean(),
  reason: z.enum(ASAP_TEXT_SKIP_REASONS).nullable(),
  reasonText: z.string().max(200).nullable(),
});
export type AsapTextRecipient = z.infer<typeof AsapTextRecipientSchema>;

export const AsapTextPreviewSchema = z.object({
  /** The suggested text (no patient name: minimum necessary). */
  body: z.string(),
  /** False between 8 PM and 8 AM office time: sending is refused. */
  sendWindowOpen: z.boolean(),
  /** Texting state for the office: when not live, texts are held, not delivered. */
  textingLive: z.boolean(),
  textingNote: z.string().nullable(),
  recipients: z.array(AsapTextRecipientSchema),
  eligibleCount: z.number().int(),
});
export type AsapTextPreview = z.infer<typeof AsapTextPreviewSchema>;

export const ASAP_TEXT_MAX_RECIPIENTS = 25;

export const AsapTextSendRequestSchema = AsapTextOpeningSchema.extend({
  idempotencyKey: z
    .string()
    .min(8)
    .max(150)
    .regex(/^[A-Za-z0-9._:-]+$/),
  patientIds: z.array(IdSchema).min(1).max(ASAP_TEXT_MAX_RECIPIENTS),
  body: z.string().trim().min(1).max(320),
}).strict();
export type AsapTextSendRequest = z.infer<typeof AsapTextSendRequestSchema>;

export const AsapTextSendResultSchema = z.object({
  results: z.array(
    z.object({
      patientId: IdSchema,
      status: z.enum(['sent', 'held', 'skipped', 'failed']),
      reason: z.string().max(200).nullable(),
      threadId: IdSchema.nullable(),
    }),
  ),
  sent: z.number().int(),
  held: z.number().int(),
  skipped: z.number().int(),
});
export type AsapTextSendResult = z.infer<typeof AsapTextSendResultSchema>;
